import React, { useState, useEffect, useRef } from 'react';
import { cmsAPI, websiteAPI, API_ORIGIN } from '../services/api';
import toast from 'react-hot-toast';
import CmsPreviewPane from '../components/cms/CmsPreviewPane';
import PublishItemModal from '../components/website/PublishItemModal';
import ImageCropperModal from '../components/common/ImageCropperModal';
import {
  Globe,
  Megaphone,
  Image as ImageIcon,
  Layout,
  PhoneCall,
  Tag,
  Award,
  BookOpen,
  Compass,
  Save,
  Upload,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  ExternalLink,
  Sparkles,
  Home,
  LayoutGrid,
  HelpCircle,
  Truck,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  Gift,
  Mail,
  Layers,
  ShieldCheck,
  FileText,
  Package,
  Eye,
  EyeOff,
  Star,
  Search,
  MoreVertical,
  Check,
  Pencil,
  ShoppingBag
} from 'lucide-react';

const PAGE_LINK_OPTIONS = [
  { label: "Pick Existing Page Link...", value: "" },
  { label: "All Jewellery (/jewellery)", value: "/jewellery" },
  { label: "Women's Jewellery (/jewellery?gender=women)", value: "/jewellery?gender=women" },
  { label: "Men's Jewellery (/jewellery?gender=men)", value: "/jewellery?gender=men" },
  { label: "Rings (/jewellery?category=Rings)", value: "/jewellery?category=Rings" },
  { label: "Necklaces (/jewellery?category=Necklaces)", value: "/jewellery?category=Necklaces" },
  { label: "Earrings (/jewellery?category=Earrings)", value: "/jewellery?category=Earrings" },
  { label: "Bangles & Cuffs (/jewellery?category=Bangles)", value: "/jewellery?category=Bangles" },
  { label: "Accessories (/jewellery?category=Accessories)", value: "/jewellery?category=Accessories" },
  { label: "Collections (/collections)", value: "/collections" },
  { label: "Atelier & Craft (/atelier)", value: "/atelier" },
  { label: "High Silver (/high-silver)", value: "/high-silver" },
  { label: "About Us (/about)", value: "/about" },
  { label: "Blog & Journal (/blog)", value: "/blog" }
];

// Categories the Accessories page can be pointed at
const ACCESSORY_CATEGORY_OPTIONS = ['Watches', 'Brooch', 'Accessories', 'Rings', 'Necklaces', 'Bangles', 'Kada', 'Earrings', 'Pendants', 'Bracelets', 'Anklets'];

// The two product rails on the website home page. A piece sits in one of them at a time.
const HOME_CAROUSEL_OPTIONS = [
  { value: 'discover_collection', label: 'Discover the Collection' },
  { value: 'discover_curb_collection', label: 'Discover the Curb Collection' }
];

const homeCarouselLabel = (value) =>
  (HOME_CAROUSEL_OPTIONS.find((o) => o.value === value) || {}).label || '';

/**
 * Editor for the plain repeating blocks — the house pillars, the timeline and
 * anything else that is a short list of text fields. Every entry can be
 * reordered, hidden or removed.
 */
/**
 * A section's settings as a list.
 *
 * An unconfigured section is stored as `{}`, not `[]`, and `{}` is truthy — so
 * the `|| []` this replaces let an object through, and the next spread or map
 * over it threw "list is not iterable" the moment somebody pressed Add.
 */
function asList(value) {
  return Array.isArray(value) ? value : [];
}

function SimpleListEditor({ items, fields, onChange, addLabel = 'Add Item', itemLabel = 'Item', makeItem }) {
  const list = Array.isArray(items) ? items : [];

  const patch = (idx, key, value) => {
    const updated = [...list];
    updated[idx] = { ...updated[idx], [key]: value };
    onChange(updated);
  };

  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const updated = [...list];
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    onChange(updated);
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
          {itemLabel}s ({list.length})
        </h3>
        <button
          type="button"
          onClick={() => onChange([...list, makeItem ? makeItem() : { isActive: true }])}
          style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={13} /> {addLabel}
        </button>
      </div>

      {list.map((item, idx) => (
        <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, background: '#f8fafc', display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>{itemLabel} {idx + 1}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: item.isActive === false ? '#94a3b8' : '#16a34a', cursor: 'pointer' }}>
                <input type="checkbox" checked={item.isActive !== false} onChange={(e) => patch(idx, 'isActive', e.target.checked)} />
                {item.isActive === false ? 'Hidden' : 'Live'}
              </label>
              <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}>
                <ArrowUp size={13} />
              </button>
              <button type="button" onClick={() => move(idx, 1)} disabled={idx === list.length - 1}
                style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === list.length - 1 ? 'not-allowed' : 'pointer' }}>
                <ArrowDown size={13} />
              </button>
              <button type="button" onClick={() => onChange(list.filter((_, i) => i !== idx))}
                style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: fields.filter((f) => !f.full).map((f) => (f.width ? f.width + 'px' : '1fr')).join(' ') || '1fr', gap: 10 }}>
            {fields.filter((f) => !f.full).map((f) => (
              <div key={f.key}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>{f.label}</label>
                <input
                  type="text"
                  value={item[f.key] || ''}
                  onChange={(e) => patch(idx, f.key, e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }}
                />
              </div>
            ))}
          </div>

          {fields.filter((f) => f.full).map((f) => (
            <div key={f.key}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea
                  rows={2}
                  value={item[f.key] || ''}
                  onChange={(e) => patch(idx, f.key, e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3, fontFamily: 'inherit' }}
                />
              ) : (
                <input
                  type="text"
                  value={item[f.key] || ''}
                  onChange={(e) => patch(idx, f.key, e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }}
                />
              )}
            </div>
          ))}
        </div>
      ))}

      {list.length === 0 && (
        <div style={{ textAlign: 'center', padding: 30, color: '#64748b', fontSize: 12.5, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10 }}>
          Nothing here yet - this block will not appear on the website.
        </div>
      )}
    </div>
  );
}

/**
 * True once a section keeps its own list of buttons, which makes the single
 * Button Text / Link pair above it dead weight — it is not what the page reads
 * any more, and leaving it on screen invites somebody to fill it in and wonder
 * why nothing changed.
 */
function hasOwnButtonList(section) {
  const extra = section?.extraConfigParsed;
  if (!extra || Array.isArray(extra) || typeof extra !== 'object') return false;
  return Array.isArray(extra.buttons) && extra.buttons.length > 0;
}

export function getStorefrontUrl() {
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return process.env.REACT_APP_STOREFRONT_URL || 'http://localhost:3001';
  }
  return process.env.REACT_APP_STOREFRONT_URL || 'https://cq.letinaisolution.com';
}

const STOREFRONT = getStorefrontUrl();

// One size for every action in a published-item row, so the group reads as a
// row of controls rather than a scatter of boxes
const rowAction = {
  width: 32,
  height: 32,
  minWidth: 32,
  minHeight: 32,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#fff',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  cursor: 'pointer',
  padding: 0,
  boxSizing: 'border-box',
};

// Separates ordering from state from links, so the eye is not mistaken for the
// button beside it
const rowDivider = {
  width: 1,
  height: 20,
  flexShrink: 0,
  background: '#cbd5e1',
  margin: '0 2px',
};

export default function WebsiteCmsPage() {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState({});
  const [activeTab, setActiveTab] = useState('announcement_bar');
  const [activeMegaSubTab, setActiveMegaSubTab] = useState('men');
  const [savingKey, setSavingKey] = useState(null);
  const [cropperModalData, setCropperModalData] = useState(null);

  const fetchCmsSections = async () => {
    setLoading(true);
    try {
      const res = await cmsAPI.getAllSections();
      const rawList = res.data?.data || res.data || [];
      const map = {};
      rawList.forEach((s) => {
        let extra = [];
        try {
          extra = typeof s.extraConfigJson === 'string' ? JSON.parse(s.extraConfigJson || '[]') : (s.extraConfigJson || []);
        } catch {
          extra = [];
        }
        map[s.sectionKey] = { ...s, extraConfigParsed: extra };
      });
      setSections(map);
    } catch {
      toast.error('Failed to load CMS sections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCmsSections();
  }, []);

  // A section seeded after this page was opened would show an empty editor
  // until a browser refresh — pull the list again the first time it is opened.
  const refetchedTabs = useRef(new Set());
  useEffect(() => {
    if (loading || !activeTab) return;
    if (activeTab === 'published_items' || activeTab === 'hover_mega_menu') return;
    if (sections[activeTab] || refetchedTabs.current.has(activeTab)) return;
    refetchedTabs.current.add(activeTab);
    fetchCmsSections();
  }, [activeTab, loading, sections]);

  // An edit lives in this page's memory until Publish is pressed. Nothing else
  // tracked that, so somebody could change three tabs, publish once, and lose
  // the other two without a word — which is exactly what happened.
  const [dirtySections, setDirtySections] = useState(() => new Set());

  const markDirty = (key) =>
    setDirtySections((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));

  const markSaved = (key) =>
    setDirtySections((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  const handleUpdateField = (key, field, val) => {
    markDirty(key);
    setSections((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: val
      }
    }));
  };

  const handleUpdateExtra = (key, updatedExtra) => {
    markDirty(key);
    setSections((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        extraConfigParsed: updatedExtra
      }
    }));
  };

  const handleSaveSection = async (key) => {
    const target = sections[key];
    if (!target) return;
    setSavingKey(key);
    try {
      const payload = {
        title: target.title,
        subtitle: target.subtitle,
        badgeText: target.badgeText,
        buttonText: target.buttonText,
        buttonLink: target.buttonLink,
        mediaUrl: target.mediaUrl,
        backgroundColor: target.backgroundColor,
        textColor: target.textColor,
        isActive: target.isActive,
        sortOrder: target.sortOrder,
        extraConfigJson:
          typeof target.extraConfigParsed === 'string'
            ? target.extraConfigParsed
            : JSON.stringify(target.extraConfigParsed !== undefined ? target.extraConfigParsed : {})
      };
      await cmsAPI.updateSection(key, payload);
      markSaved(key);
      toast.success(`${target.sectionName || key} updated live!`);
      setPreviewReloadToken((t) => t + 1);
      fetchCmsSections();
    } catch {
      toast.error('Failed to save CMS changes');
    } finally {
      setSavingKey(null);
    }
  };

  const [uploadingItemKey, setUploadingItemKey] = useState(null);

  const openCropperModalForFile = (file, sectionKey, onCropped) => {
    const objectUrl = URL.createObjectURL(file);
    const aspect = sectionKey === 'hero_slider' ? (16 / 9) : 1;
    setCropperModalData({
      imageSrc: objectUrl,
      title: 'Crop & Resize Image',
      aspect: aspect,
      onCrop: (croppedFile) => {
        croppedFile._isCropped = true;
        onCropped(croppedFile);
      }
    });
  };

  const handleMediaUpload = async (key, file, toastId = null) => {
    if (!file) return;
    const tId = toastId || toast.loading('Uploading & optimizing photo...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await cmsAPI.uploadMedia(key, formData);
      const mediaUrl = res.data?.data || res.data?.message;
      handleUpdateField(key, 'mediaUrl', mediaUrl);
      toast.success('Media uploaded successfully!', { id: tId });
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload media asset', { id: tId });
    }
  };

  const handleMoveExtraItem = (key, idx, direction) => {
    const stored = sections[key]?.extraConfigParsed;
    const list = Array.isArray(stored) ? stored : [];
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= list.length) return;
    const updated = [...list];
    const temp = updated[idx];
    updated[idx] = updated[targetIdx];
    updated[targetIdx] = temp;
    handleUpdateExtra(key, updated);
  };

  const handleFileUploadForItem = async (sectionKey, itemIdx, fieldName, file, toastId = null) => {
    if (!file) return;
    const tId = toastId || toast.loading('Uploading & optimizing photo...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      // One item's photo must not overwrite the section's own banner image
      formData.append('applyToSection', itemIdx === null || itemIdx < 0);
      const res = await cmsAPI.uploadMedia(sectionKey, formData);
      const uploadedUrl = res.data?.data || res.data?.message;
      if (itemIdx !== null && itemIdx >= 0) {
        const stored = asList(sections[sectionKey]?.extraConfigParsed);
        let baseList = stored.length > 0 ? stored : [];
        if (sectionKey === 'accessories_categories' && baseList.length < 2) {
          baseList = [
            { label: 'Watches', media: '/images/editorial/category-watches.jpg', href: '/jewellery?category=Watches', subtitle: 'Masterpiece silver timepieces with precision dials.' },
            { label: 'Brooch', media: '/images/editorial/story.jpg', href: '/jewellery?category=Brooch', subtitle: 'Hand-sculpted floral and deco brooches set with brilliant pavé.' }
          ];
        }
        const updated = [...baseList];
        if (!updated[itemIdx]) updated[itemIdx] = {};
        updated[itemIdx][fieldName] = uploadedUrl;
        updated[itemIdx].media = uploadedUrl;
        updated[itemIdx].mediaUrl = uploadedUrl;
        updated[itemIdx].image = uploadedUrl;
        handleUpdateExtra(sectionKey, updated);
        await handleSaveSection(sectionKey);
      } else {
        handleUpdateField(sectionKey, 'mediaUrl', uploadedUrl);
      }
      toast.success('Photo uploaded & updated!', { id: tId });
    } catch (err) {
      console.error(err);
      toast.error('Photo upload failed', { id: tId });
    }
  };

  const handleFileUpload = async (e, sectionKey, itemIdx = null, fieldName = 'mediaUrl') => {
    const file = e?.target?.files?.[0] || (e instanceof File ? e : null);
    if (!file) return;
    const uploadKey = `${sectionKey}_${itemIdx ?? 'main'}_${fieldName}`;
    setUploadingItemKey(uploadKey);
    const toastId = toast.loading('Uploading & optimizing photo...');
    try {
      if (itemIdx !== null && itemIdx >= 0) {
        await handleFileUploadForItem(sectionKey, itemIdx, fieldName, file, toastId);
      } else {
        await handleMediaUpload(sectionKey, file, toastId);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload photo', { id: toastId });
    } finally {
      setUploadingItemKey(null);
      if (e?.target) {
        try { e.target.value = ''; } catch {}
      }
    }
  };

  // ── Published products (Purchase > Item View > Publish to Website) ──
  const [publishedItems, setPublishedItems] = useState([]);
  const [publishedLoading, setPublishedLoading] = useState(false);
  const [publishedSearch, setPublishedSearch] = useState('');
  const [publishedDirty, setPublishedDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [openRowMenu, setOpenRowMenu] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const fetchPublishedItems = async () => {
    setPublishedLoading(true);
    try {
      const res = await websiteAPI.getPublishedItems({});
      setPublishedItems(res.data?.data || []);
      setPublishedDirty(false);
    } catch {
      toast.error('Failed to load published products');
    } finally {
      setPublishedLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'published_items') fetchPublishedItems();
  }, [activeTab]);

  const resolveItemImage = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const movePublishedItem = (idx, direction) => {
    const target = idx + direction;
    if (target < 0 || target >= publishedItems.length) return;
    const list = [...publishedItems];
    [list[idx], list[target]] = [list[target], list[idx]];
    setPublishedItems(list);
    setPublishedDirty(true);
  };

  const toggleItemFeatured = (idx) => {
    const list = [...publishedItems];
    list[idx] = { ...list[idx], isFeatured: !list[idx].isFeatured };
    setPublishedItems(list);
    setPublishedDirty(true);
  };

  const toggleItemVisibility = async (item) => {
    try {
      await websiteAPI.toggleWebsiteFlag(item.purchaseDetailID, !item.showOnWebsite);
      toast.success(!item.showOnWebsite ? 'Piece is back on the website' : 'Piece hidden from the website');
      fetchPublishedItems();
      refreshPreview();
    } catch {
      toast.error('Failed to change visibility');
    }
  };

  // Row pencil opens the shared "Publish Item to Public Website" popup
  const handleEditSaved = (form) => {
    if (!editItem) return;
    // Patch the row in place so an unsaved running order is not thrown away
    setPublishedItems((list) => list.map((it) => (
      it.purchaseDetailID === editItem.purchaseDetailID
        ? {
            ...it,
            webTitle: form.webTitle || it.webTitle,
            displayPrice: Number(form.displayPrice) || 0,
            category: form.webCategory || it.category,
            gender: form.gender || it.gender,
            tileSize: form.tileSize || it.tileSize,
            seoSlug: form.seoSlug || it.seoSlug,
            isFeatured: Boolean(form.isFeatured),
            showOnWebsite: Boolean(form.showOnWebsite)
          }
        : it
    )));
    refreshPreview();
  };

  // 3-dot row menu — one home page rail per piece, saved the moment it is picked
  // so it never depends on the "Save Running Order" button.
  const changeItemHomeSection = async (item, section) => {
    const next = item.homeSection === section ? '' : section;
    setOpenRowMenu(null);
    try {
      await websiteAPI.setItemHomeSection(item.purchaseDetailID, next);
      setPublishedItems((list) => list.map((it) => (
        it.purchaseDetailID === item.purchaseDetailID ? { ...it, homeSection: next } : it
      )));
      toast.success(next ? `Added to ${homeCarouselLabel(next)}` : 'Removed from the home page');
      refreshPreview();
    } catch {
      toast.error('Failed to save the home page placement');
    }
  };

  const savePublishedOrder = async () => {
    setSavingOrder(true);
    try {
      const payload = publishedItems.map((it, idx) => ({
        purchaseDetailID: it.purchaseDetailID,
        sortOrder: idx,
        isFeatured: Boolean(it.isFeatured)
      }));
      await websiteAPI.savePublishedOrder(payload);
      toast.success('Website running order saved');
      fetchPublishedItems();
      refreshPreview();
    } catch {
      toast.error('Failed to save the order');
    } finally {
      setSavingOrder(false);
    }
  };

  const visiblePublishedItems = publishedItems.filter((it) => {
    if (!publishedSearch.trim()) return true;
    const q = publishedSearch.trim().toLowerCase();
    return `${it.webTitle} ${it.skuNo} ${it.category}`.toLowerCase().includes(q);
  });

  // Live preview — every tab listed here previews the storefront page it edits
  const HOMEPAGE_PREVIEW_TABS = [
    'hero_slider',
    'editorial_banner_1',
    'jewellery_categories',
    'editorial_banner_2',
    'gifting_banner',
    'at_your_service'
  ];
  const JEWELLERY_PREVIEW_TABS = [
    'published_items',
    'page_jewellery',
    'grid_banners',
    'jewellery_seo_block'
  ];
  const [previewOpen, setPreviewOpen] = useState(true);
  const [previewReloadToken, setPreviewReloadToken] = useState(0);
  const PREVIEW_PAGE_BY_TAB = {
    ...Object.fromEntries(HOMEPAGE_PREVIEW_TABS.map((t) => [t, '/'])),
    ...Object.fromEntries(JEWELLERY_PREVIEW_TABS.map((t) => [t, '/jewellery'])),
    page_accessories: '/accessories',
    accessories_categories: '/accessories',
    page_collections: '/collections',
    // Header and footer sit on every page, so they preview against the home page
    announcement_bar: '/',
    nav_menu: '/',
    hover_mega_menu: '/',
    search_bar: '/',
    promo_popup: '/',
    footer_info: '/',
    newsletter_banner: '/',
    page_privacy: '/privacy',
    privacy_clauses: '/privacy',
    page_terms: '/terms',
    terms_clauses: '/terms',
    page_accessibility: '/accessibility',
    accessibility_clauses: '/accessibility',
    care_hub: '/customer-care',
    care_shipping: '/customer-care/shipping-delivery',
    care_returns: '/customer-care/returns-exchanges',
    care_repairs: '/customer-care/repairs-care',
    care_faqs: '/customer-care/faqs',
    // The contact panel is on all five, so it previews against the hub
    care_contact: '/customer-care',
    page_blog: '/blog',
    blog_articles: '/blog',
    blog_quote: '/blog',
    page_atelier: '/atelier',
    page_about: '/about',
    about_marks: '/about',
    about_banner: '/about',
    about_philosophy: '/about',
    about_founder: '/about',
    about_numbers: '/about',
    about_timeline: '/about',
    about_contact: '/about',
    atelier_gallery: '/atelier',
    atelier_marks: '/atelier',
    atelier_process: '/atelier',
    atelier_artisans: '/atelier',
    atelier_salon: '/atelier',
    high_silver_hero: '/high-silver',
    high_silver_categories: '/high-silver',
    high_silver_chapters: '/high-silver',
    high_silver_collection: '/high-silver',
    high_silver_appointment: '/high-silver'
  };
  const previewPagePath = PREVIEW_PAGE_BY_TAB[activeTab] || '/';
  const canPreview = Boolean(PREVIEW_PAGE_BY_TAB[activeTab]);
  const showPreview = previewOpen && canPreview;

  // Products are not CMS sections, so the frame has to re-render to show them
  const refreshPreview = () => setPreviewReloadToken((t) => t + 1);

  // Editing a hover panel? Ask the frame to hold that panel open.
  const MEGA_PANEL_BY_SUBTAB = {
    men: 'men',
    women: 'women',
    collections: 'collections',
    accessories: 'accessories',
    high_silver: 'high_silver',
    atelier: 'atelier',
    world_of_cq: 'about'
  };
  const previewPanel = activeTab === 'hover_mega_menu'
    ? (MEGA_PANEL_BY_SUBTAB[activeMegaSubTab] || null)
    : null;

  // The running order as it stands in this list — arrows move pieces here long
  // before "Save Running Order" is pressed, and the preview follows along.
  // Featured pieces lead, exactly as the catalog query orders them after a save.
  const previewProductOrder = publishedItems
    .filter((it) => it.showOnWebsite && !it.isSold)
    .map((it, idx) => ({ it, idx }))
    .sort((a, b) => (Number(b.it.isFeatured) - Number(a.it.isFeatured)) || (a.idx - b.idx))
    .map(({ it }) => it.seoSlug)
    .filter(Boolean);

  // Section search across every page in the navigation
  const [tabSearch, setTabSearch] = useState('');

  const pageGroups = [
    {
      id: 'homepage',
      label: 'Homepage',
      icon: Home,
      tabs: [
        { key: 'hero_slider', label: '1. Hero Sliders', icon: ImageIcon },
        { key: 'editorial_banner_1', label: '2. Sculpted Sterling Banner 1', icon: Layers },
        { key: 'jewellery_categories', label: '3. Shop by Category Showcase', icon: Sparkles },
        { key: 'editorial_banner_2', label: '4. Curb Chain & Heavy Silver Banner 2', icon: Layers },
        { key: 'gifting_banner', label: '5. The Art of Gifting Banner 3', icon: Gift },
        { key: 'at_your_service', label: '6. At Your Service Concierge', icon: ShieldCheck }
      ]
    },
    {
      id: 'page_collections',
      label: 'Collections',
      icon: Layout,
      tabs: [{ key: 'page_collections', label: 'Collections Page & Cards', icon: Layout }]
    },
    {
      id: 'page_jewellery',
      label: 'Jewellery',
      icon: Sparkles,
      tabs: [
        { key: 'published_items', label: 'Published Products', icon: Package },
        { key: 'page_jewellery', label: 'Jewellery Catalog Banner', icon: Sparkles },
        { key: 'grid_banners', label: 'Catalog Grid Campaign Banners', icon: LayoutGrid },
        { key: 'jewellery_seo_block', label: 'SEO Footer Block', icon: BookOpen }
      ]
    },
    {
      id: 'page_accessories',
      label: 'Accessories',
      icon: Tag,
      tabs: [
        { key: 'page_accessories', label: 'Accessories Page Banner', icon: Tag },
        { key: 'accessories_categories', label: 'Categories On This Page', icon: LayoutGrid }
      ]
    },
    {
      id: 'page_high_silver',
      label: 'High Silver',
      icon: Layers,
      tabs: [
        { key: 'high_silver_hero', label: 'Hero Banner', icon: ImageIcon },
        { key: 'high_silver_categories', label: 'Categories Bar', icon: LayoutGrid },
        { key: 'high_silver_chapters', label: 'Chapter Banners', icon: Layers },
        { key: 'high_silver_collection', label: 'The Collection Section', icon: ShoppingBag },
        { key: 'high_silver_appointment', label: 'Appointment Band', icon: Award }
      ]
    },
    {
      id: 'page_atelier',
      label: 'Atelier',
      icon: BookOpen,
      tabs: [
        { key: 'page_atelier', label: 'Atelier Page Banner', icon: BookOpen },
        { key: 'atelier_gallery', label: 'Gallery Items', icon: ImageIcon },
        { key: 'atelier_marks', label: 'Bench Marks', icon: Award },
        { key: 'atelier_process', label: 'The Process', icon: Compass },
        { key: 'atelier_artisans', label: 'Meet the Artisans', icon: ImageIcon },
        { key: 'atelier_salon', label: 'Visit the Salon', icon: PhoneCall }
      ]
    },
    {
      id: 'page_blog',
      label: 'Blog',
      icon: BookOpen,
      tabs: [
        { key: 'page_blog', label: 'Blog Page Banner', icon: BookOpen },
        { key: 'blog_articles', label: 'Blog Posts', icon: Layout },
        { key: 'blog_quote', label: 'Philosophy Quote', icon: Award }
      ]
    },
    {
      id: 'page_about',
      label: 'WORLD OF CQ',
      icon: Compass,
      tabs: [
        { key: 'page_about', label: 'Page Banner', icon: Compass },
        { key: 'about_marks', label: 'House Marks', icon: Award },
        { key: 'about_banner', label: 'Atelier Banner', icon: ImageIcon },
        { key: 'about_philosophy', label: 'House Philosophy', icon: Award },
        { key: 'about_founder', label: 'Word from the Bench', icon: BookOpen },
        { key: 'about_numbers', label: 'House in Numbers', icon: Award },
        { key: 'about_timeline', label: 'History Timeline', icon: BookOpen },
        { key: 'about_contact', label: 'Contact Band', icon: PhoneCall }
      ]
    },
    {
      id: 'header_global',
      label: 'Header & Global',
      icon: Compass,
      tabs: [
        { key: 'announcement_bar', label: 'Announcement Bar', icon: Megaphone },
        { key: 'nav_menu', label: 'Header Navigation Menu', icon: Compass },
        { key: 'hover_mega_menu', label: 'Hover Mega-Menu Manager', icon: LayoutGrid },
        { key: 'search_bar', label: 'Search Bar & Quick Links', icon: Search },
        { key: 'promo_popup', label: 'Promo Alert Popups', icon: Tag }
      ]
    },
    {
      id: 'legal',
      label: 'Legal Pages',
      icon: ShieldCheck,
      tabs: [
        { key: 'page_privacy',          label: 'Privacy — Banner',        icon: ShieldCheck },
        { key: 'privacy_clauses',       label: 'Privacy — Clauses',       icon: FileText },
        { key: 'page_terms',            label: 'Terms — Banner',          icon: ShieldCheck },
        { key: 'terms_clauses',         label: 'Terms — Clauses',         icon: FileText },
        { key: 'page_accessibility',    label: 'Accessibility — Banner',  icon: ShieldCheck },
        { key: 'accessibility_clauses', label: 'Accessibility — Clauses', icon: FileText }
      ]
    },
    {
      id: 'customer_care',
      label: 'Customer Care',
      icon: HelpCircle,
      tabs: [
        { key: 'care_hub',      label: 'Help Hub Page',        icon: LayoutGrid },
        { key: 'care_shipping', label: 'Shipping & Delivery',  icon: Truck },
        { key: 'care_returns',  label: 'Returns & Exchanges',  icon: RotateCcw },
        { key: 'care_repairs',  label: 'Repairs & Care',       icon: Sparkles },
        { key: 'care_faqs',     label: 'FAQs',                 icon: HelpCircle },
        { key: 'care_contact',  label: 'Contact Panel',        icon: PhoneCall }
      ]
    },
    {
      id: 'footer',
      label: 'Footer',
      icon: PhoneCall,
      tabs: [
        { key: 'footer_info', label: 'Footer & Contacts', icon: PhoneCall },
        { key: 'newsletter_banner', label: 'Newsletter Signup Band', icon: Mail }
      ]
    }
  ];

  // The same warning when the whole tab is closed or reloaded
  useEffect(() => {
    if (dirtySections.size === 0) return;
    const onLeave = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [dirtySections]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Globe className="spin" size={32} style={{ marginBottom: 12 }} />
        <p style={{ fontWeight: 600 }}>Loading Website CMS Configuration...</p>
      </div>
    );
  }

  // Moving away from an unpublished section is the moment the work is lost, so
  // it is the moment to say so. Every tab switch goes through here.
  const leaveTab = (nextKey) => {
    if (nextKey === activeTab) return;

    if (dirtySections.has(activeTab)) {
      const name = sections[activeTab]?.sectionName || activeTab;
      const stay = !window.confirm(
        `"${name}" has changes you have not published yet.\n\n` +
        `Leave this tab and lose them?\n\n` +
        `Cancel, then press "Publish Section Live" to keep them.`
      );
      if (stay) return;

      // They chose to drop the edits, so the section goes back to what is stored
      markSaved(activeTab);
      fetchCmsSections();
    }

    setActiveTab(nextKey);
  };

  const getTargetSaveKey = () => {
    if (activeTab === 'hover_mega_menu') {
      const subMap = {
        men: 'page_men',
        women: 'page_women',
        collections: 'mega_collections',
        accessories: 'page_accessories',
        high_silver: 'page_high_silver',
        atelier: 'page_atelier',
        world_of_cq: 'page_about'
      };
      return subMap[activeMegaSubTab] || 'nav_menu';
    }
    return activeTab;
  };

  const resolveImgPreviewUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/api/')) return `${API_ORIGIN}${url}`;
    if (url.startsWith('/website/cms/')) return `http://localhost:5002${url}`;
    const sf = getStorefrontUrl();
    if (url.startsWith('/images/')) return `${sf}${url}`;
    if (url.startsWith('images/')) return `${sf}/${url}`;
    if (!url.startsWith('/')) return `${sf}/images/editorial/${url}`;
    return `${sf}${url}`;
  };

  const currentSection = (activeTab === 'hover_mega_menu' ? sections[getTargetSaveKey()] : sections[activeTab]) || {};

  // Whichever page holds the open section is the page shown as selected
  const activeGroup = pageGroups.find((g) => g.tabs.some((t) => t.key === activeTab)) || pageGroups[0];

  const railQuery = tabSearch.trim().toLowerCase();
  const searchHits = railQuery
    ? pageGroups.flatMap((g) => g.tabs
        .filter((t) => t.label.toLowerCase().includes(railQuery) || t.key.includes(railQuery))
        .map((t) => ({ ...t, groupLabel: g.label })))
    : null;

  return (
    <div style={{ padding: showPreview ? '18px 20px' : '24px 32px', maxWidth: showPreview ? '100%' : 1400, margin: '0 auto' }}>
      {/* Header Bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0, lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Globe color="#2563eb" size={28} /> Page-Wise Website CMS Manager
          </h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: '8px 0 0 0', lineHeight: 1.5 }}>
            Manage headlines, hero sliders, category cards, and headers live on{' '}
            <a
              href={STOREFRONT}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#2563eb', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              thecaratquest.com <ExternalLink size={12} />
            </a>
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {canPreview && (
          <button
            type="button"
            onClick={() => setPreviewOpen((v) => !v)}
            style={{
              background: previewOpen ? '#eff6ff' : '#fff',
              color: previewOpen ? '#1d4ed8' : '#475569',
              border: `1px solid ${previewOpen ? '#93c5fd' : '#cbd5e1'}`,
              borderRadius: 8,
              padding: '10px 16px',
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            {previewOpen ? 'Hide Preview' : 'Show Live Preview'}
          </button>
        )}
        <button
          onClick={() => activeTab === 'published_items' ? savePublishedOrder() : handleSaveSection(getTargetSaveKey())}
          disabled={activeTab === 'published_items' ? (savingOrder || !publishedDirty) : savingKey === getTargetSaveKey()}
          style={{
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '10px 20px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 4px 12px rgba(37,99,235,0.25)'
          }}
        >
          <Save size={16} />
          {activeTab === 'published_items'
            ? (savingOrder ? 'Saving Order...' : publishedDirty ? 'Save Running Order' : 'Order Saved')
            : savingKey === activeTab ? 'Publishing Live...' : 'Publish Section Live'}
        </button>
        </div>
      </div>

      {/* Navigation — pages on one line, that page's sections on the next.
          Nothing scrolls sideways: a row that runs out of width wraps. */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5, flex: 1, minWidth: 0 }}>
            {pageGroups.map((group) => {
              const GroupIcon = group.icon;
              const isActive = activeGroup.id === group.id;
              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => { setTabSearch(''); if (group.tabs[0]) leaveTab(group.tabs[0].key); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, whiteSpace: 'nowrap',
                    padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: isActive ? '#0f172a' : 'transparent',
                    color: isActive ? '#fff' : '#64748b',
                    fontSize: 12.5, fontWeight: isActive ? 800 : 700,
                    transition: 'all 0.15s ease'
                  }}
                >
                  <GroupIcon size={14} color={isActive ? '#fff' : '#94a3b8'} />
                  {group.label}
                </button>
              );
            })}
          </div>

          <div style={{ position: 'relative', width: 190, flexShrink: 0 }}>
            <Search size={13} style={{ position: 'absolute', left: 9, top: 9, color: '#94a3b8' }} />
            <input
              type="text"
              value={tabSearch}
              onChange={(e) => setTabSearch(e.target.value)}
              placeholder="Find a section..."
              style={{ width: '100%', padding: '7px 10px 7px 28px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, background: '#f8fafc' }}
            />
          </div>
        </div>

        <div style={{ height: 1, background: '#e2e8f0', margin: '8px 2px' }} />

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5 }}>
          {searchHits && searchHits.length === 0 && (
            <span style={{ padding: '8px 10px', fontSize: 12, color: '#94a3b8' }}>Nothing matches that.</span>
          )}

          {(searchHits || activeGroup.tabs).map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { leaveTab(t.key); setTabSearch(''); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, whiteSpace: 'nowrap',
                  padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: isActive ? '#2563eb' : '#f8fafc',
                  color: isActive ? '#fff' : '#475569',
                  fontSize: 12, fontWeight: isActive ? 700 : 600,
                  transition: 'all 0.15s ease'
                }}
              >
                <Icon size={14} color={isActive ? '#fff' : '#94a3b8'} />
                {t.label}
                {t.groupLabel && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: isActive ? 'rgba(255,255,255,0.7)' : '#94a3b8' }}>
                    · {t.groupLabel}
                  </span>
                )}
                {/* Unpublished work, visible without opening the tab */}
                {dirtySections.has(t.key) && (
                  <span
                    title="Not published yet"
                    style={{
                      width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                      background: isActive ? '#fff' : '#f59e0b',
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Editor + Live Preview */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: showPreview ? 'minmax(0, 1fr) minmax(340px, 440px)' : '1fr',
          gap: 16,
          alignItems: 'start'
        }}
      >
        {/* Content Tab Details */}
        <div
          className={showPreview ? 'cms-editor-narrow' : undefined}
          style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: showPreview ? 18 : 24, minWidth: 0 }}
        >
          {/* Active Switch Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f1f5f9' }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                {currentSection.sectionName || activeTab}
              </h2>
              <span style={{ fontSize: 11, color: '#64748b' }}>Key: <code>{getTargetSaveKey()}</code></span>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: currentSection.isActive ? '#16a34a' : '#94a3b8' }}>
              <input
                type="checkbox"
                checked={currentSection.isActive ?? true}
                onChange={(e) => handleUpdateField(getTargetSaveKey(), 'isActive', e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              <span>{currentSection.isActive ? 'Active Live on Website' : 'Hidden / Disabled'}</span>
            </label>
          </div>

          {/* TAB 1: Announcement Bar */}
          {activeTab === 'announcement_bar' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Announcement Bar Text</label>
                <input
                  type="text"
                  value={currentSection.title || ''}
                  onChange={(e) => handleUpdateField('announcement_bar', 'title', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Button / Action Label</label>
                  <input
                    type="text"
                    value={currentSection.buttonText || ''}
                    onChange={(e) => handleUpdateField('announcement_bar', 'buttonText', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Action Target Link</label>
                  <input
                    type="text"
                    value={currentSection.buttonLink || ''}
                    onChange={(e) => handleUpdateField('announcement_bar', 'buttonLink', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
              </div>

              {/* The two colours sit together, above a strip showing them as the
                  bar will actually render — text picked against a background you
                  cannot see is how a site ends up with black on black. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Background Color</label>
                  <input
                    type="color"
                    value={currentSection.backgroundColor || '#000000'}
                    onChange={(e) => handleUpdateField('announcement_bar', 'backgroundColor', e.target.value)}
                    style={{ width: '100%', height: 42, borderRadius: 8, border: '1px solid #cbd5e1', cursor: 'pointer', marginTop: 4 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Text Color</label>
                  <input
                    type="color"
                    value={currentSection.textColor || '#ffffff'}
                    onChange={(e) => handleUpdateField('announcement_bar', 'textColor', e.target.value)}
                    style={{ width: '100%', height: 42, borderRadius: 8, border: '1px solid #cbd5e1', cursor: 'pointer', marginTop: 4 }}
                  />
                </div>
              </div>

              <div
                style={{
                  backgroundColor: currentSection.backgroundColor || '#000000',
                  color: currentSection.textColor || '#ffffff',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  padding: '10px 14px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <span>{currentSection.title || 'Announcement bar text'}</span>
                {currentSection.buttonText && (
                  <span style={{ textDecoration: 'underline' }}>{currentSection.buttonText}</span>
                )}
              </div>
            </div>
          )}

          {/* TAB: Legal page banners */}
          {['page_privacy', 'page_terms', 'page_accessibility'].includes(activeTab) && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Eyebrow</label>
                <input
                  type="text"
                  value={currentSection.badgeText || ''}
                  onChange={(e) => handleUpdateField(activeTab, 'badgeText', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Page Title</label>
                <input
                  type="text"
                  value={currentSection.title || ''}
                  onChange={(e) => handleUpdateField(activeTab, 'title', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Intro Line</label>
                <textarea
                  rows={2}
                  value={currentSection.subtitle || ''}
                  onChange={(e) => handleUpdateField(activeTab, 'subtitle', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>
            </div>
          )}

          {/* TAB: Legal clauses — the prose of a policy page */}
          {['privacy_clauses', 'terms_clauses', 'accessibility_clauses'].includes(activeTab) && (() => {
            const stored = currentSection.extraConfigParsed;
            const clauses = Array.isArray(stored) ? stored : [];
            const update = (next) => handleUpdateExtra(activeTab, next);

            return (
              <div style={{ display: 'grid', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Last Updated</label>
                  <input
                    type="text"
                    value={currentSection.badgeText || ''}
                    placeholder="1 September 2026"
                    onChange={(e) => handleUpdateField(activeTab, 'badgeText', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                  <p style={{ fontSize: 10.5, color: '#94a3b8', margin: '3px 0 0' }}>
                    Shown above the clauses. Change it whenever you change the wording.
                  </p>
                </div>

                <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, background: '#f8fafc' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Clauses ({clauses.length})</label>
                    <button type="button" className="btn btn-sm btn-ghost"
                      onClick={() => update([...clauses, { heading: '', body: '' }])}>
                      <Plus size={13} /> Add Clause
                    </button>
                  </div>

                  <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 12px', lineHeight: 1.6 }}>
                    {clauses.length === 0
                      ? 'Empty — this page is showing the wording built into the website. Add a clause here and this list takes over the whole page, so add all of them before publishing.'
                      : 'This list is the page, in this order. Leave a blank line inside a clause to start a new paragraph.'}
                  </p>

                  <div style={{ display: 'grid', gap: 10 }}>
                    {clauses.map((clause, idx) => (
                      <div key={idx} style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, padding: 10, display: 'grid', gap: 8 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          <span className="text-mono" style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', paddingTop: 8 }}>
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          <input
                            type="text"
                            value={clause.heading || ''}
                            placeholder="Clause heading"
                            onChange={(e) => { const n = [...clauses]; n[idx] = { ...n[idx], heading: e.target.value }; update(n); }}
                            style={{ flex: 1, padding: '7px 9px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12, fontWeight: 700 }}
                          />
                          <button type="button" className="btn btn-sm btn-ghost" disabled={idx === 0}
                            onClick={() => handleMoveExtraItem(activeTab, idx, -1)}><ArrowUp size={13} /></button>
                          <button type="button" className="btn btn-sm btn-ghost" disabled={idx === clauses.length - 1}
                            onClick={() => handleMoveExtraItem(activeTab, idx, 1)}><ArrowDown size={13} /></button>
                          <button type="button" className="btn btn-sm btn-danger"
                            onClick={() => update(clauses.filter((_, i) => i !== idx))}><Trash2 size={13} /></button>
                        </div>
                        <textarea
                          rows={5}
                          value={clause.body || ''}
                          placeholder="The wording of this clause."
                          onChange={(e) => { const n = [...clauses]; n[idx] = { ...n[idx], body: e.target.value }; update(n); }}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12, lineHeight: 1.6 }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* TAB: Customer Care pages — questions, answers, and the panel beside them */}
          {['care_hub', 'care_shipping', 'care_returns', 'care_repairs', 'care_faqs'].includes(activeTab) && (() => {
            const stored = currentSection.extraConfigParsed;
            const extra = (stored && !Array.isArray(stored) && typeof stored === 'object') ? stored : {};
            const faqs = Array.isArray(extra.faqs) ? extra.faqs : [];
            const cats = Array.isArray(extra.categories) ? extra.categories : [];
            const steps = Array.isArray(extra.steps) ? extra.steps : [];
            const patch = (part) => handleUpdateExtra(activeTab, { ...extra, ...part });

            const move = (list, key, idx, direction) => {
              const t = idx + direction;
              if (t < 0 || t >= list.length) return;
              const next = [...list];
              next[idx] = list[t];
              next[t] = list[idx];
              patch({ [key]: next });
            };

            return (
              <div style={{ display: 'grid', gap: 18 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Page Heading</label>
                  <input
                    type="text"
                    value={currentSection.title || ''}
                    onChange={(e) => handleUpdateField(activeTab, 'title', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Intro Line</label>
                  <textarea
                    rows={2}
                    value={currentSection.subtitle || ''}
                    onChange={(e) => handleUpdateField(activeTab, 'subtitle', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>

                {/* The hub's tiles */}
                {activeTab === 'care_hub' && (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, background: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Category Tiles ({cats.length})</label>
                      <button type="button" className="btn btn-sm btn-ghost"
                        onClick={() => patch({ categories: [...cats, { label: 'NEW TILE', href: '/customer-care/faqs', icon: 'receipt' }] })}>
                        <Plus size={13} /> Add Tile
                      </button>
                    </div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {cats.map((cat, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px auto', gap: 8, alignItems: 'end', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, padding: 9 }}>
                          <div>
                            <label style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b' }}>Label</label>
                            <input type="text" value={cat.label || ''}
                              onChange={(e) => { const n = [...cats]; n[idx] = { ...n[idx], label: e.target.value }; patch({ categories: n }); }}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b' }}>Link</label>
                            <input type="text" value={cat.href || ''}
                              onChange={(e) => { const n = [...cats]; n[idx] = { ...n[idx], href: e.target.value }; patch({ categories: n }); }}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b' }}>Icon</label>
                            <select value={cat.icon || 'receipt'}
                              onChange={(e) => { const n = [...cats]; n[idx] = { ...n[idx], icon: e.target.value }; patch({ categories: n }); }}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}>
                              <option value="truck">Delivery van</option>
                              <option value="return">Return arrows</option>
                              <option value="sparkle">Sparkle</option>
                              <option value="receipt">Receipt</option>
                              <option value="pin">Shop</option>
                              <option value="phone">Telephone</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', gap: 5 }}>
                            <button type="button" className="btn btn-sm btn-ghost" disabled={idx === 0} onClick={() => move(cats, 'categories', idx, -1)}><ArrowUp size={13} /></button>
                            <button type="button" className="btn btn-sm btn-ghost" disabled={idx === cats.length - 1} onClick={() => move(cats, 'categories', idx, 1)}><ArrowDown size={13} /></button>
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => patch({ categories: cats.filter((_, i) => i !== idx) })}><Trash2 size={13} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* How the repair service works */}
                {activeTab === 'care_repairs' && (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, background: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Service Steps ({steps.length})</label>
                      <button type="button" className="btn btn-sm btn-ghost"
                        onClick={() => patch({ steps: [...steps, { title: 'NEW STEP', body: '' }] })}>
                        <Plus size={13} /> Add Step
                      </button>
                    </div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {steps.map((step, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, alignItems: 'end', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, padding: 9 }}>
                          <div>
                            <label style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b' }}>Step {idx + 1}</label>
                            <input type="text" value={step.title || ''}
                              onChange={(e) => { const n = [...steps]; n[idx] = { ...n[idx], title: e.target.value }; patch({ steps: n }); }}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b' }}>What happens</label>
                            <input type="text" value={step.body || ''}
                              onChange={(e) => { const n = [...steps]; n[idx] = { ...n[idx], body: e.target.value }; patch({ steps: n }); }}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }} />
                          </div>
                          <div style={{ display: 'flex', gap: 5 }}>
                            <button type="button" className="btn btn-sm btn-ghost" disabled={idx === 0} onClick={() => move(steps, 'steps', idx, -1)}><ArrowUp size={13} /></button>
                            <button type="button" className="btn btn-sm btn-ghost" disabled={idx === steps.length - 1} onClick={() => move(steps, 'steps', idx, 1)}><ArrowDown size={13} /></button>
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => patch({ steps: steps.filter((_, i) => i !== idx) })}><Trash2 size={13} /></button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Eyebrow above the steps</label>
                        <input type="text" value={currentSection.badgeText || ''}
                          onChange={(e) => handleUpdateField(activeTab, 'badgeText', e.target.value)}
                          style={{ width: '100%', padding: '7px 9px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 3 }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Button</label>
                          <input type="text" value={currentSection.buttonText || ''}
                            onChange={(e) => handleUpdateField(activeTab, 'buttonText', e.target.value)}
                            style={{ width: '100%', padding: '7px 9px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 3 }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Link</label>
                          <input type="text" value={currentSection.buttonLink || ''}
                            onChange={(e) => handleUpdateField(activeTab, 'buttonLink', e.target.value)}
                            style={{ width: '100%', padding: '7px 9px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 3 }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* The questions themselves */}
                {activeTab !== 'care_hub' && (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, background: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Questions &amp; Answers ({faqs.length})</label>
                      <button type="button" className="btn btn-sm btn-ghost"
                        onClick={() => patch({ faqs: [...faqs, { q: '', a: '' }] })}>
                        <Plus size={13} /> Add Question
                      </button>
                    </div>
                    <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 12px', lineHeight: 1.6 }}>
                      Leave a blank line inside an answer to start a new paragraph. A question with no answer is not shown on the website.
                    </p>

                    <div style={{ display: 'grid', gap: 10 }}>
                      {faqs.map((faq, idx) => (
                        <div key={idx} style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, padding: 10, display: 'grid', gap: 8 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <span className="text-mono" style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', paddingTop: 8 }}>
                              {String(idx + 1).padStart(2, '0')}
                            </span>
                            <input
                              type="text"
                              value={faq.q || ''}
                              placeholder="What is your return policy?"
                              onChange={(e) => { const n = [...faqs]; n[idx] = { ...n[idx], q: e.target.value }; patch({ faqs: n }); }}
                              style={{ flex: 1, padding: '7px 9px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12, fontWeight: 700 }}
                            />
                            <button type="button" className="btn btn-sm btn-ghost" disabled={idx === 0} onClick={() => move(faqs, 'faqs', idx, -1)}><ArrowUp size={13} /></button>
                            <button type="button" className="btn btn-sm btn-ghost" disabled={idx === faqs.length - 1} onClick={() => move(faqs, 'faqs', idx, 1)}><ArrowDown size={13} /></button>
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => patch({ faqs: faqs.filter((_, i) => i !== idx) })}><Trash2 size={13} /></button>
                          </div>
                          <textarea
                            rows={3}
                            value={faq.a || ''}
                            placeholder="Write the answer as you would say it to a customer."
                            onChange={(e) => { const n = [...faqs]; n[idx] = { ...n[idx], a: e.target.value }; patch({ faqs: n }); }}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12, lineHeight: 1.6 }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* TAB: Customer Care — how to reach the shop */}
          {activeTab === 'care_contact' && (() => {
            const stored = currentSection.extraConfigParsed;
            const extra = (stored && !Array.isArray(stored) && typeof stored === 'object') ? stored : {};
            const patch = (part) => handleUpdateExtra('care_contact', { ...extra, ...part });

            const FIELDS = [
              { key: 'whatsapp', label: 'WhatsApp Number', hint: 'Country code, no + or spaces \u2014 e.g. 919426210080' },
              { key: 'phone',    label: 'Telephone',       hint: 'Shown and dialled as written' },
              { key: 'email',    label: 'Email',           hint: '' },
              { key: 'hours',    label: 'Opening Hours',   hint: '' },
              { key: 'showroom', label: 'Showroom Address', hint: '' },
            ];

            return (
              <div style={{ display: 'grid', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Panel Heading</label>
                  <input
                    type="text"
                    value={currentSection.title || ''}
                    onChange={(e) => handleUpdateField('care_contact', 'title', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>

                <p style={{ fontSize: 11.5, color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                  This panel appears on the hub and on all four help pages. Leave a field empty and that row simply
                  does not appear \u2014 better than a telephone number nobody answers.
                </p>

                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{f.label}</label>
                    <input
                      type="text"
                      value={extra[f.key] || ''}
                      onChange={(e) => patch({ [f.key]: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                    />
                    {f.hint && <p style={{ fontSize: 10.5, color: '#94a3b8', margin: '3px 0 0' }}>{f.hint}</p>}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* TAB: Search Bar & Quick Links */}
          {activeTab === 'search_bar' && (
            <div style={{ display: 'grid', gap: 20 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Search Input Placeholder Text</label>
                <input
                  type="text"
                  value={currentSection.title || 'What can we help you with?'}
                  onChange={(e) => handleUpdateField('search_bar', 'title', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Header Tagline / Badge</label>
                <input
                  type="text"
                  value={currentSection.badgeText || 'INSTANT SEARCH & CATALOG DISCOVERY'}
                  onChange={(e) => handleUpdateField('search_bar', 'badgeText', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>

              <SimpleListEditor
                items={currentSection.extraConfigParsed || [
                  { title: "Different Types of Rings for Men", image: "/images/editorial/craft-1.jpg", href: "/blog" },
                  { title: "Everyday Jewelry Essentials", image: "/images/editorial/story.jpg", href: "/blog" },
                  { title: "The Art of the Stack", image: "/images/editorial/about.jpg", href: "/blog" }
                ]}
                itemLabel="Popular Search Story"
                addLabel="Add Search Story"
                makeItem={() => ({ title: 'New Search Story', image: '/images/editorial/story.jpg', href: '/blog', isActive: true })}
                fields={[
                  { key: 'title', label: 'Story Title' },
                  { key: 'href', label: 'Link URL' },
                  { key: 'image', label: 'Image URL', full: true }
                ]}
                onChange={(updated) => handleUpdateExtra('search_bar', updated)}
              />
            </div>
          )}

          {/* TAB 2: Homepage Hero Slider */}
          {activeTab === 'hero_slider' && (
            <div style={{ display: 'grid', gap: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>Hero Slide Banners ({currentSection.extraConfigParsed?.length || 0})</h3>
                <button
                  type="button"
                  onClick={() => {
                    const list = asList(currentSection.extraConfigParsed);
                    const newItem = {
                      id: Date.now(),
                      badge: 'NEW COLLECTION',
                      title: 'New Hero Banner Headline',
                      subtitle: 'Add description subtitle here...',
                      buttonText: 'EXPLORE NOW →',
                      buttonLink: '/jewellery',
                      mediaUrl: '/images/hero/hero-bg-1.jpg'
                    };
                    handleUpdateExtra('hero_slider', [...list, newItem]);
                  }}
                  style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Plus size={14} /> Add Hero Slide
                </button>
              </div>

              {(asList(currentSection.extraConfigParsed)).map((slide, idx) => (
                <div key={slide.id || idx} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, background: '#f8fafc', position: 'relative', display: 'grid', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #cbd5e1', paddingBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontWeight: 800, fontSize: 13, color: '#2563eb' }}>Hero Slide #{idx + 1}</span>
                      <select
                        value={slide.isSplit ? 'split' : 'single'}
                        onChange={(e) => {
                          const updated = [...asList(currentSection.extraConfigParsed)];
                          updated[idx].isSplit = e.target.value === 'split';
                          handleUpdateExtra('hero_slider', updated);
                        }}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 11, fontWeight: 700, background: slide.isSplit ? '#fef3c7' : '#e0f2fe', color: slide.isSplit ? '#92400e' : '#0369a1' }}
                      >
                        <option value="single">Single Full Width Banner</option>
                        <option value="split">Split 50/50 Dual Banner (Women + Men)</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => handleMoveExtraItem('hero_slider', idx, -1)}
                        style={{ background: idx === 0 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: idx === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <ArrowUp size={12} /> Move Up
                      </button>
                      <button
                        type="button"
                        disabled={idx === (currentSection.extraConfigParsed?.length || 1) - 1}
                        onClick={() => handleMoveExtraItem('hero_slider', idx, 1)}
                        style={{ background: idx === (currentSection.extraConfigParsed?.length || 1) - 1 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: idx === (currentSection.extraConfigParsed?.length || 1) - 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <ArrowDown size={12} /> Move Down
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (asList(currentSection.extraConfigParsed)).filter((_, i) => i !== idx);
                          handleUpdateExtra('hero_slider', updated);
                        }}
                        style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer', marginLeft: 8 }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {slide.isSplit ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      {/* LEFT SIDE FORM */}
                      <div style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: 12, background: '#fff', display: 'grid', gap: 10 }}>
                        <div style={{ fontWeight: 800, fontSize: 12, color: '#0369a1', borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>Left Side Banner (e.g. Women)</div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Left Gold Badge</label>
                          <input
                            type="text"
                            value={slide.leftBadge || slide.badge || "WOMEN'S CAMPAIGN"}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].leftBadge = e.target.value;
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Left Headline Title</label>
                          <input
                            type="text"
                            value={slide.leftTitle || slide.title || "CQ HERITAGE WOMEN"}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].leftTitle = e.target.value;
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Left Subtitle</label>
                          <input
                            type="text"
                            value={slide.leftSubtitle || slide.subtitle || ""}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].leftSubtitle = e.target.value;
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Button Text</label>
                            <input
                              type="text"
                              value={slide.leftButtonText || slide.buttonText || "SHOP WOMEN →"}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].leftButtonText = e.target.value;
                                handleUpdateExtra('hero_slider', updated);
                              }}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Target Link</label>
                            <input
                              type="text"
                              value={slide.leftButtonLink || slide.buttonLink || "/jewellery?gender=women"}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].leftButtonLink = e.target.value;
                                handleUpdateExtra('hero_slider', updated);
                              }}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                            />
                            <select
                              value={slide.leftButtonLink || ''}
                              onChange={(e) => {
                                if (e.target.value) {
                                  const updated = [...asList(currentSection.extraConfigParsed)];
                                  updated[idx].leftButtonLink = e.target.value;
                                  handleUpdateExtra('hero_slider', updated);
                                }
                              }}
                              style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 10, background: '#f8fafc', color: '#334155', marginTop: 3 }}
                            >
                              {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                              <option key={oIdx} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Left Cover Photo</label>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input
                              type="text"
                              value={slide.leftMediaUrl || slide.mediaUrl || ""}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].leftMediaUrl = e.target.value;
                                handleUpdateExtra('hero_slider', updated);
                              }}
                              style={{ flex: 1, padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                            />
                            <label style={{ background: '#2563eb', color: '#fff', padding: '6px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Upload size={12} /> Upload
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => e.target.files?.[0] && handleFileUploadForItem('hero_slider', idx, 'leftMediaUrl', e.target.files[0])}
                              />
                            </label>
                          </div>
                          {(slide.leftMediaUrl || slide.mediaUrl) && (
                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <img
                                src={resolveImgPreviewUrl(slide.leftMediaUrl || slide.mediaUrl)}
                                alt="Left Preview"
                                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }}
                              />
                              <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>✓ Image Ready</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* RIGHT SIDE FORM */}
                      <div style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: 12, background: '#fff', display: 'grid', gap: 10 }}>
                        <div style={{ fontWeight: 800, fontSize: 12, color: '#92400e', borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>Right Side Banner (e.g. Men)</div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Right Gold Badge</label>
                          <input
                            type="text"
                            value={slide.rightBadge || "MEN'S CAMPAIGN"}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].rightBadge = e.target.value;
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Right Headline Title</label>
                          <input
                            type="text"
                            value={slide.rightTitle || "CQ STERLING MEN"}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].rightTitle = e.target.value;
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Right Subtitle</label>
                          <input
                            type="text"
                            value={slide.rightSubtitle || ""}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].rightSubtitle = e.target.value;
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Button Text</label>
                            <input
                              type="text"
                              value={slide.rightButtonText || "SHOP MEN →"}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].rightButtonText = e.target.value;
                                handleUpdateExtra('hero_slider', updated);
                              }}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Target Link</label>
                            <input
                              type="text"
                              value={slide.rightButtonLink || "/jewellery?gender=men"}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].rightButtonLink = e.target.value;
                                handleUpdateExtra('hero_slider', updated);
                              }}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                            />
                            <select
                              value={slide.rightButtonLink || ''}
                              onChange={(e) => {
                                if (e.target.value) {
                                  const updated = [...asList(currentSection.extraConfigParsed)];
                                  updated[idx].rightButtonLink = e.target.value;
                                  handleUpdateExtra('hero_slider', updated);
                                }
                              }}
                              style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 10, background: '#f8fafc', color: '#334155', marginTop: 3 }}
                            >
                              {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                              <option key={oIdx} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Right Cover Photo</label>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input
                              type="text"
                              value={slide.rightMediaUrl || ""}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].rightMediaUrl = e.target.value;
                                handleUpdateExtra('hero_slider', updated);
                              }}
                              style={{ flex: 1, padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                            />
                            <label style={{ background: '#2563eb', color: '#fff', padding: '6px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Upload size={12} /> Upload
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => e.target.files?.[0] && handleFileUploadForItem('hero_slider', idx, 'rightMediaUrl', e.target.files[0])}
                              />
                            </label>
                          </div>
                          {slide.rightMediaUrl && (
                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <img
                                src={resolveImgPreviewUrl(slide.rightMediaUrl)}
                                alt="Right Preview"
                                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }}
                              />
                              <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>✓ Image Ready</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Top Badge Text</label>
                          <input
                            type="text"
                            value={slide.badge || ''}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].badge = e.target.value;
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Headline Title</label>
                          <input
                            type="text"
                            value={slide.title || ''}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].title = e.target.value;
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                          />
                        </div>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Subtitle Narrative</label>
                        <input
                          type="text"
                          value={slide.subtitle || ''}
                          onChange={(e) => {
                            const updated = [...asList(currentSection.extraConfigParsed)];
                            updated[idx].subtitle = e.target.value;
                            handleUpdateExtra('hero_slider', updated);
                          }}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginTop: 10 }}>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>CTA Button Text</label>
                          <input
                            type="text"
                            value={slide.buttonText || ''}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].buttonText = e.target.value;
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Target URL Link</label>
                          <input
                            type="text"
                            value={slide.buttonLink || ''}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].buttonLink = e.target.value;
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                          />
                          <select
                            value={slide.buttonLink || ''}
                            onChange={(e) => {
                              if (e.target.value) {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].buttonLink = e.target.value;
                                handleUpdateExtra('hero_slider', updated);
                              }
                            }}
                            style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 10, background: '#f8fafc', color: '#334155', marginTop: 3 }}
                          >
                            {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                            <option key={oIdx} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Background Photo</label>
                          <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                            <input
                              type="text"
                              value={slide.mediaUrl || ''}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].mediaUrl = e.target.value;
                                handleUpdateExtra('hero_slider', updated);
                              }}
                              style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                            />
                            <label style={{ background: '#2563eb', color: '#fff', padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                              <Upload size={12} /> Upload
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => e.target.files?.[0] && handleFileUploadForItem('hero_slider', idx, 'mediaUrl', e.target.files[0])}
                              />
                            </label>
                          </div>
                          {slide.mediaUrl && (
                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <img
                                src={resolveImgPreviewUrl(slide.mediaUrl)}
                                alt="Preview"
                                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }}
                              />
                              <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>✓ Image Ready</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ marginTop: 12, background: '#f1f5f9', padding: 10, borderRadius: 6 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>Dark Overlay Opacity (0% = Clear Photo, 100% = Solid Black Gradient)</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={slide.overlayOpacity ?? 65}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].overlayOpacity = Number(e.target.value);
                              handleUpdateExtra('hero_slider', updated);
                            }}
                            style={{ flex: 1, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', width: 45, textAlign: 'right' }}>{slide.overlayOpacity ?? 65}%</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* TAB: Jewellery Categories Showcase */}
          {activeTab === 'jewellery_categories' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  Jewellery Category Showcase Cards ({currentSection.extraConfigParsed?.length || 0})
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    const list = asList(currentSection.extraConfigParsed);
                    const newItem = {
                      key: `cat_${Date.now()}`,
                      label: 'New Category',
                      title: 'Category Subtitle Narrative',
                      mediaUrl: '/images/categories/rings.jpg',
                      link: '/jewellery?category=new',
                      isActive: true
                    };
                    handleUpdateExtra('jewellery_categories', [...list, newItem]);
                  }}
                  style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Plus size={14} /> Add Category
                </button>
              </div>

              {(asList(currentSection.extraConfigParsed)).map((cat, idx) => (
                <div key={cat.key || idx} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, background: '#f8fafc', position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color: '#2563eb' }}>
                      Category #{idx + 1}: {cat.label}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => handleMoveExtraItem('jewellery_categories', idx, -1)}
                        style={{ background: idx === 0 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: idx === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <ArrowUp size={12} /> Move Up
                      </button>
                      <button
                        type="button"
                        disabled={idx === (currentSection.extraConfigParsed?.length || 1) - 1}
                        onClick={() => handleMoveExtraItem('jewellery_categories', idx, 1)}
                        style={{ background: idx === (currentSection.extraConfigParsed?.length || 1) - 1 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: idx === (currentSection.extraConfigParsed?.length || 1) - 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <ArrowDown size={12} /> Move Down
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (asList(currentSection.extraConfigParsed)).filter((_, i) => i !== idx);
                          handleUpdateExtra('jewellery_categories', updated);
                        }}
                        style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer', marginLeft: 8 }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Category Name / Label</label>
                      <input
                        type="text"
                        value={cat.label || ''}
                        onChange={(e) => {
                          const updated = [...asList(currentSection.extraConfigParsed)];
                          updated[idx].label = e.target.value;
                          handleUpdateExtra('jewellery_categories', updated);
                        }}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Subtitle / Description</label>
                      <input
                        type="text"
                        value={cat.title || ''}
                        onChange={(e) => {
                          const updated = [...asList(currentSection.extraConfigParsed)];
                          updated[idx].title = e.target.value;
                          handleUpdateExtra('jewellery_categories', updated);
                        }}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Category Cover Photo</label>
                      <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                        <input
                          type="text"
                          value={cat.mediaUrl || ''}
                          onChange={(e) => {
                            const updated = [...asList(currentSection.extraConfigParsed)];
                            updated[idx].mediaUrl = e.target.value;
                            handleUpdateExtra('jewellery_categories', updated);
                          }}
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                        />
                        <label style={{ background: '#2563eb', color: '#fff', padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          <Upload size={12} /> Upload
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => e.target.files?.[0] && handleFileUploadForItem('jewellery_categories', idx, 'mediaUrl', e.target.files[0])}
                          />
                        </label>
                      </div>
                      {cat.mediaUrl && (
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <img
                            src={resolveImgPreviewUrl(cat.mediaUrl)}
                            alt="Preview"
                            style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }}
                          />
                          <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>✓ Image Active</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, marginTop: 10, alignItems: 'center' }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Target Store Link</label>
                      <input
                        type="text"
                        value={cat.link || ''}
                        onChange={(e) => {
                          const updated = [...asList(currentSection.extraConfigParsed)];
                          updated[idx].link = e.target.value;
                          handleUpdateExtra('jewellery_categories', updated);
                        }}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                      />
                    </div>
                    <div style={{ paddingTop: 18 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, color: cat.isActive ? '#16a34a' : '#94a3b8' }}>
                        <input
                          type="checkbox"
                          checked={cat.isActive ?? true}
                          onChange={(e) => {
                            const updated = [...asList(currentSection.extraConfigParsed)];
                            updated[idx].isActive = e.target.checked;
                            handleUpdateExtra('jewellery_categories', updated);
                          }}
                        />
                        Show Category
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* TAB: At Your Service Concierge */}
          {activeTab === 'at_your_service' && (
            <div style={{ display: 'grid', gap: 20 }}>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, display: 'grid', gap: 14 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>Concierge Section Banner Title</h3>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Section Title / Headline</label>
                  <input
                    type="text"
                    value={currentSection.title || 'AT YOUR SERVICE'}
                    onChange={(e) => handleUpdateField('at_your_service', 'title', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                    Concierge Service Badges ({Array.isArray(currentSection.extraConfigParsed) ? currentSection.extraConfigParsed.length : 0})
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      const list = Array.isArray(currentSection.extraConfigParsed) ? currentSection.extraConfigParsed : [];
                      const newItem = {
                        title: 'New Service',
                        subtitle: 'Service subtitle description...',
                        href: '/about'
                      };
                      handleUpdateExtra('at_your_service', [...list, newItem]);
                    }}
                    style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Plus size={14} /> Add Service Item
                  </button>
                </div>

                {(Array.isArray(currentSection.extraConfigParsed) ? currentSection.extraConfigParsed : []).map((srv, idx, arr) => (
                  <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, background: '#f8fafc', display: 'grid', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#2563eb' }}>Service #{idx + 1}: {srv.title || 'Untitled'}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => handleMoveExtraItem('at_your_service', idx, -1)}
                          style={{ background: idx === 0 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: idx === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          <ArrowUp size={12} /> Move Up
                        </button>
                        <button
                          type="button"
                          disabled={idx === arr.length - 1}
                          onClick={() => handleMoveExtraItem('at_your_service', idx, 1)}
                          style={{ background: idx === arr.length - 1 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: idx === arr.length - 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          <ArrowDown size={12} /> Move Down
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = arr.filter((_, i) => i !== idx);
                            handleUpdateExtra('at_your_service', updated);
                          }}
                          style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer', marginLeft: 8 }}
                          title="Delete Service"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '130px 1.2fr 1.5fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Select Icon</label>
                        <select
                          value={srv.iconKey || srv.icon || 'truck'}
                          onChange={(e) => {
                            const updated = [...arr];
                            updated[idx].iconKey = e.target.value;
                            updated[idx].icon = e.target.value;
                            handleUpdateExtra('at_your_service', updated);
                          }}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2, background: '#fff' }}
                        >
                          <option value="truck">Shipping</option>
                          <option value="clock">Appointment</option>
                          <option value="home">Boutique</option>
                          <option value="message">WhatsApp</option>
                          <option value="gift">Gift Box</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Service Title</label>
                        <input
                          type="text"
                          value={srv.title || ''}
                          onChange={(e) => {
                            const updated = [...arr];
                            updated[idx].title = e.target.value;
                            handleUpdateExtra('at_your_service', updated);
                          }}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Subtitle / Tagline</label>
                        <input
                          type="text"
                          value={srv.subtitle || ''}
                          onChange={(e) => {
                            const updated = [...arr];
                            updated[idx].subtitle = e.target.value;
                            handleUpdateExtra('at_your_service', updated);
                          }}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Target Link URL</label>
                        <input
                          type="text"
                          value={srv.href || ''}
                          onChange={(e) => {
                            const updated = [...arr];
                            updated[idx].href = e.target.value;
                            handleUpdateExtra('at_your_service', updated);
                          }}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                        />
                        <select
                          value={srv.href || ''}
                          onChange={(e) => {
                            if (e.target.value) {
                              const updated = [...arr];
                              updated[idx].href = e.target.value;
                              handleUpdateExtra('at_your_service', updated);
                            }
                          }}
                          style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 10, background: '#f8fafc', color: '#334155', marginTop: 3 }}
                        >
                          {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                          <option key={oIdx} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 4: Footer Contacts */}
          {activeTab === 'footer_info' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Customer Care Email</label>
                  <input
                    type="text"
                    value={currentSection.extraConfigParsed?.email || ''}
                    onChange={(e) => {
                      const updated = { ...currentSection.extraConfigParsed, email: e.target.value };
                      handleUpdateExtra('footer_info', updated);
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Contact Phone Number</label>
                  <input
                    type="text"
                    value={currentSection.extraConfigParsed?.phone || ''}
                    onChange={(e) => {
                      const updated = { ...currentSection.extraConfigParsed, phone: e.target.value };
                      handleUpdateExtra('footer_info', updated);
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>WhatsApp Contact Number</label>
                  <input
                    type="text"
                    value={currentSection.extraConfigParsed?.whatsapp || ''}
                    onChange={(e) => {
                      const updated = { ...currentSection.extraConfigParsed, whatsapp: e.target.value };
                      handleUpdateExtra('footer_info', updated);
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Head Office Address (Surat)</label>
                  <input
                    type="text"
                    value={currentSection.extraConfigParsed?.address || ''}
                    placeholder="206, 2nd Floor, Krink Tower, Mangadh Chowk, Varachha, Surat, Gujarat 395006"
                    onChange={(e) => {
                      const updated = { ...currentSection.extraConfigParsed, address: e.target.value };
                      handleUpdateExtra('footer_info', updated);
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Sales Office Address (Mumbai BKC)</label>
                  <input
                    type="text"
                    value={currentSection.extraConfigParsed?.salesOfficeAddress || ''}
                    placeholder="Office no. JE 3290, Tower- JE, G Block BKC, Bandra Kurla Complex, Bandra East, Mumbai, Maharashtra 400051"
                    onChange={(e) => {
                      const updated = { ...currentSection.extraConfigParsed, salesOfficeAddress: e.target.value };
                      handleUpdateExtra('footer_info', updated);
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Instagram Link</label>
                  <input
                    type="text"
                    value={currentSection.extraConfigParsed?.instagram || ''}
                    onChange={(e) => {
                      const updated = { ...currentSection.extraConfigParsed, instagram: e.target.value };
                      handleUpdateExtra('footer_info', updated);
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>YouTube Link</label>
                  <input
                    type="text"
                    value={currentSection.extraConfigParsed?.youtube || ''}
                    onChange={(e) => {
                      const updated = { ...currentSection.extraConfigParsed, youtube: e.target.value };
                      handleUpdateExtra('footer_info', updated);
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Facebook Link</label>
                  <input
                    type="text"
                    value={currentSection.extraConfigParsed?.facebook || ''}
                    onChange={(e) => {
                      const updated = { ...currentSection.extraConfigParsed, facebook: e.target.value };
                      handleUpdateExtra('footer_info', updated);
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>WhatsApp Social Link</label>
                  <input
                    type="text"
                    value={currentSection.extraConfigParsed?.whatsappLink || ''}
                    onChange={(e) => {
                      const updated = { ...currentSection.extraConfigParsed, whatsappLink: e.target.value };
                      handleUpdateExtra('footer_info', updated);
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>LinkedIn Link</label>
                  <input
                    type="text"
                    value={currentSection.extraConfigParsed?.linkedin || ''}
                    onChange={(e) => {
                      const updated = { ...currentSection.extraConfigParsed, linkedin: e.target.value };
                      handleUpdateExtra('footer_info', updated);
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 8: Header Navigation Menu & Mega-Menus */}
          {activeTab === 'nav_menu' && (
            <div style={{ display: 'grid', gap: 24 }}>
              {/* Header Navigation Links */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>Header Navigation Bar Links</h3>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0 0' }}>Main navigation items shown in the top header bar.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const isArr = Array.isArray(currentSection.extraConfigParsed);
                      const list = isArr
                        ? (asList(currentSection.extraConfigParsed))
                        : (currentSection.extraConfigParsed?.navItems || []);
                      const newItem = { label: 'New Menu Item', href: '/jewellery', isActive: true };
                      const updated = [...list, newItem];
                      if (isArr) {
                        handleUpdateExtra('nav_menu', updated);
                      } else {
                        handleUpdateExtra('nav_menu', { ...(currentSection.extraConfigParsed || {}), navItems: updated });
                      }
                    }}
                    style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Plus size={14} /> Add Menu Item
                  </button>
                </div>

                <div style={{ display: 'grid', gap: 8 }}>
                  {(() => {
                    const extraNavList = Array.isArray(currentSection.extraConfigParsed)
                      ? currentSection.extraConfigParsed
                      : (currentSection.extraConfigParsed?.navItems && Array.isArray(currentSection.extraConfigParsed.navItems))
                        ? currentSection.extraConfigParsed.navItems
                        : [];

                    const writeNav = (updated) => {
                      if (Array.isArray(currentSection.extraConfigParsed)) {
                        handleUpdateExtra('nav_menu', updated);
                      } else {
                        handleUpdateExtra('nav_menu', { ...currentSection.extraConfigParsed, navItems: updated });
                      }
                    };

                    const moveNavItem = (idx, direction) => {
                      const target = idx + direction;
                      if (target < 0 || target >= extraNavList.length) return;
                      const updated = [...extraNavList];
                      [updated[idx], updated[target]] = [updated[target], updated[idx]];
                      writeNav(updated);
                    };

                    return extraNavList.map((item, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 90px 180px 40px', gap: 10, alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, background: '#f8fafc' }}>
                        <input
                          type="text"
                          placeholder="Menu Label (e.g. COLLECTIONS)"
                          value={item.label || ''}
                          onChange={(e) => {
                            const updated = [...extraNavList];
                            updated[idx].label = e.target.value;
                            if (Array.isArray(currentSection.extraConfigParsed)) {
                              handleUpdateExtra('nav_menu', updated);
                            } else {
                              handleUpdateExtra('nav_menu', { ...currentSection.extraConfigParsed, navItems: updated });
                            }
                          }}
                          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                        />
                        <input
                          type="text"
                          placeholder="Target Link (e.g. /collections)"
                          value={item.href || ''}
                          onChange={(e) => {
                            const updated = [...extraNavList];
                            updated[idx].href = e.target.value;
                            if (Array.isArray(currentSection.extraConfigParsed)) {
                              handleUpdateExtra('nav_menu', updated);
                            } else {
                              handleUpdateExtra('nav_menu', { ...currentSection.extraConfigParsed, navItems: updated });
                            }
                          }}
                          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                        />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, color: item.isActive ? '#16a34a' : '#94a3b8' }}>
                          <input
                            type="checkbox"
                            checked={item.isActive ?? true}
                            onChange={(e) => {
                              const updated = [...extraNavList];
                              updated[idx].isActive = e.target.checked;
                              if (Array.isArray(currentSection.extraConfigParsed)) {
                                handleUpdateExtra('nav_menu', updated);
                              } else {
                                handleUpdateExtra('nav_menu', { ...currentSection.extraConfigParsed, navItems: updated });
                              }
                            }}
                          />
                          Show
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <button
                            type="button"
                            disabled={idx === 0}
                            onClick={() => moveNavItem(idx, -1)}
                            style={{ background: idx === 0 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: idx === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}
                          >
                            <ArrowUp size={12} /> Up
                          </button>
                          <button
                            type="button"
                            disabled={idx === (extraNavList.length || 1) - 1}
                            onClick={() => moveNavItem(idx, 1)}
                            style={{ background: idx === (extraNavList.length || 1) - 1 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: idx === (extraNavList.length || 1) - 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}
                          >
                            <ArrowDown size={12} /> Down
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = extraNavList.filter((_, i) => i !== idx);
                            if (Array.isArray(currentSection.extraConfigParsed)) {
                              handleUpdateExtra('nav_menu', updated);
                            } else {
                              handleUpdateExtra('nav_menu', { ...currentSection.extraConfigParsed, navItems: updated });
                            }
                          }}
                          style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                          title="Delete Menu Link"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* TAB 8.5: Dedicated Hover Mega-Menu Manager */}
          {activeTab === 'hover_mega_menu' && (
            <div style={{ display: 'grid', gap: 20 }}>
              {/* Top Header Selector Buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, background: '#f1f5f9', padding: 6, borderRadius: 10, border: '1px solid #cbd5e1' }}>
                {[
                  { key: 'men', label: 'Men Hover Dropdown' },
                  { key: 'women', label: 'Women Hover Dropdown' },
                  { key: 'collections', label: 'Collections Hover Dropdown' },
                  { key: 'accessories', label: 'Accessories Hover Dropdown' },
                  { key: 'high_silver', label: 'High Silver Hover Dropdown' },
                  { key: 'atelier', label: 'Atelier Hover Dropdown' },
                  { key: 'world_of_cq', label: 'WORLD OF CQ Hover Dropdown' }
                ].map(sub => (
                  <button
                    key={sub.key}
                    type="button"
                    onClick={() => setActiveMegaSubTab(sub.key)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: 'none',
                      background: activeMegaSubTab === sub.key ? '#2563eb' : 'transparent',
                      color: activeMegaSubTab === sub.key ? '#fff' : '#475569',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      boxShadow: activeMegaSubTab === sub.key ? '0 2px 6px rgba(37,99,235,0.3)' : 'none',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>

              {/* 1. JEWELLERY HOVER MEGA-MENU EDITOR */}

              {(activeMegaSubTab === 'men' || activeMegaSubTab === 'women') && (() => {
                const secKey = activeMegaSubTab === 'men' ? 'page_men' : 'page_women';
                const heading = activeMegaSubTab === 'men' ? 'Men' : 'Women';
                const links = asList(currentSection.extraConfigParsed);
                return (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, display: 'grid', gap: 16 }}>
                    <div style={{ borderBottom: '1px solid #cbd5e1', paddingBottom: 10 }}>
                      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>{heading} Hover Dropdown</h3>
                      <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0 0' }}>
                        Category links and the featured card shown when a visitor hovers <b>{heading}</b> in the header.
                      </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Column Heading / Badge</label>
                        <input
                          type="text"
                          value={currentSection.badgeText || ''}
                          placeholder={`${heading}'s Jewellery`}
                          onChange={(e) => handleUpdateField(secKey, 'badgeText', e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Featured Card Title</label>
                        <input
                          type="text"
                          value={currentSection.title || ''}
                          placeholder={`${heading}'s Collection`}
                          onChange={(e) => handleUpdateField(secKey, 'title', e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Featured Card Subtitle</label>
                      <input
                        type="text"
                        value={currentSection.subtitle || ''}
                        onChange={(e) => handleUpdateField(secKey, 'subtitle', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Featured Card Photo</label>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <input
                          type="text"
                          value={currentSection.mediaUrl || ''}
                          onChange={(e) => handleUpdateField(secKey, 'mediaUrl', e.target.value)}
                          style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                        />
                        <label style={{ background: '#2563eb', color: '#fff', padding: '10px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Upload size={14} /> Upload
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => e.target.files?.[0] && handleMediaUpload(secKey, e.target.files[0])}
                          />
                        </label>
                      </div>
                      {currentSection.mediaUrl && (
                        <img src={resolveImgPreviewUrl(currentSection.mediaUrl)} alt="Preview" style={{ marginTop: 8, width: 70, height: 70, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }} />
                      )}
                    </div>

                    <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: 14, display: 'grid', gap: 10 }}>
                      <label style={{ fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase' }}>
                        {heading} Category Links ({links.length})
                      </label>

                      {links.map((link, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 30px 30px 34px', gap: 8, alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="Label (e.g. Kada)"
                            value={link.label || ''}
                            onChange={(e) => {
                              const updated = [...links];
                              updated[idx] = { ...updated[idx], label: e.target.value };
                              handleUpdateExtra(secKey, updated);
                            }}
                            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12.5 }}
                          />
                          <input
                            type="text"
                            placeholder={`/jewellery?gender=${activeMegaSubTab}&category=Kada`}
                            value={link.href || ''}
                            onChange={(e) => {
                              const updated = [...links];
                              updated[idx] = { ...updated[idx], href: e.target.value };
                              handleUpdateExtra(secKey, updated);
                            }}
                            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
                          />
                          <button
                            type="button"
                            onClick={() => handleMoveExtraItem(secKey, idx, -1)}
                            style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 0', cursor: 'pointer' }}
                          >
                            <ArrowUp size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveExtraItem(secKey, idx, 1)}
                            style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 0', cursor: 'pointer' }}
                          >
                            <ArrowDown size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUpdateExtra(secKey, links.filter((_, i) => i !== idx))}
                            style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, padding: '5px 0', cursor: 'pointer' }}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() => handleUpdateExtra(secKey, [...links, { label: 'New Category', href: `/jewellery?gender=${activeMegaSubTab}`, isActive: true }])}
                        style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, justifySelf: 'start' }}
                      >
                        <Plus size={13} /> Add Category Link
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* OTHER HEADER MEGA-MENU EDITORS */}
              {activeMegaSubTab !== 'men' && activeMegaSubTab !== 'women' && (
                <div style={{ display: 'grid', gap: 16 }}>
                  {(() => {
                    const secKeyMap = {
                      men: 'page_men',
                      women: 'page_women',
                      collections: 'mega_collections',
                      accessories: 'page_accessories',
                      high_silver: 'page_high_silver',
                      atelier: 'page_atelier',
                      world_of_cq: 'page_about'
                    };
                    const targetSecKey = secKeyMap[activeMegaSubTab] || 'page_collections';
                    const sec = sections[targetSecKey] || {};
                    return (
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, display: 'grid', gap: 16 }}>
                        {activeMegaSubTab !== 'collections' && (
                          activeMegaSubTab === 'accessories' ? (() => {
                            const accCatSec = sections.accessories_categories || {};
                            const rawList = asList(accCatSec.extraConfigParsed);
                            const list = rawList.length > 0 ? rawList : [
                              { label: 'Watches', media: '/images/editorial/category-watches.jpg', href: '/jewellery?category=Watches', subtitle: 'Masterpiece silver timepieces with precision dials.' },
                              { label: 'Brooch', media: '/images/editorial/story.jpg', href: '/jewellery?category=Brooch', subtitle: 'Hand-sculpted floral and deco brooches set with brilliant pavé.' }
                            ];

                            const updateCard = (cardIdx, field, val) => {
                              const updated = [...list];
                              if (!updated[cardIdx]) updated[cardIdx] = {};
                              updated[cardIdx] = { ...updated[cardIdx], [field]: val };
                              if (field === 'media' || field === 'mediaUrl' || field === 'image') {
                                updated[cardIdx].media = val;
                                updated[cardIdx].mediaUrl = val;
                                updated[cardIdx].image = val;
                              }
                              handleUpdateExtra('accessories_categories', updated);
                            };

                            return (
                              <div style={{ display: 'grid', gap: 16 }}>
                                <div style={{ borderBottom: '1px solid #cbd5e1', paddingBottom: 8 }}>
                                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                                    Accessories Hover Dropdown (2 Cards 50% / 50% Layout)
                                  </h3>
                                  <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0 0' }}>
                                    Manage Card 1 (Left 50%) and Card 2 (Right 50%) displayed in the Accessories hover menu.
                                  </p>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                  {/* CARD 1 EDITOR */}
                                  <div style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: 14, background: '#fff', display: 'grid', gap: 10 }}>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: '#2563eb', textTransform: 'uppercase' }}>
                                      Card 1 (Left 50% — e.g. Watches)
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Card 1 Title</label>
                                      <input
                                        type="text"
                                        value={list[0]?.label || list[0]?.name || ''}
                                        onChange={(e) => updateCard(0, 'label', e.target.value)}
                                        placeholder="e.g. Watches"
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 3 }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Card 1 Description</label>
                                      <textarea
                                        rows={2}
                                        value={list[0]?.subtitle || list[0]?.description || ''}
                                        onChange={(e) => updateCard(0, 'subtitle', e.target.value)}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 3 }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Card 1 Target Link</label>
                                      <input
                                        type="text"
                                        value={list[0]?.href || list[0]?.link || ''}
                                        onChange={(e) => updateCard(0, 'href', e.target.value)}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 3 }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Card 1 Photo</label>
                                      <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                                        <input
                                          type="text"
                                          value={list[0]?.media || list[0]?.mediaUrl || ''}
                                          onChange={(e) => updateCard(0, 'media', e.target.value)}
                                          style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                                        />
                                        <label style={{ background: '#2563eb', color: '#fff', padding: '8px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                                          <Upload size={12} /> Upload
                                          <input type="file" accept="image/*" style={{ display: 'none' }}
                                            onChange={(e) => e.target.files?.[0] && handleFileUploadForItem('accessories_categories', 0, 'media', e.target.files[0])} />
                                        </label>
                                      </div>
                                      {(list[0]?.media || list[0]?.mediaUrl) && (
                                        <img src={resolveImgPreviewUrl(list[0]?.media || list[0]?.mediaUrl)} alt="Card 1" style={{ marginTop: 6, width: '100%', height: 70, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }} />
                                      )}
                                    </div>
                                  </div>

                                  {/* CARD 2 EDITOR */}
                                  <div style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: 14, background: '#fff', display: 'grid', gap: 10 }}>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: '#2563eb', textTransform: 'uppercase' }}>
                                      Card 2 (Right 50% — e.g. Brooch)
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Card 2 Title</label>
                                      <input
                                        type="text"
                                        value={list[1]?.label || list[1]?.name || ''}
                                        onChange={(e) => updateCard(1, 'label', e.target.value)}
                                        placeholder="e.g. Brooch"
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 3 }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Card 2 Description</label>
                                      <textarea
                                        rows={2}
                                        value={list[1]?.subtitle || list[1]?.description || ''}
                                        onChange={(e) => updateCard(1, 'subtitle', e.target.value)}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 3 }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Card 2 Target Link</label>
                                      <input
                                        type="text"
                                        value={list[1]?.href || list[1]?.link || ''}
                                        onChange={(e) => updateCard(1, 'href', e.target.value)}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 3 }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>Card 2 Photo</label>
                                      <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                                        <input
                                          type="text"
                                          value={list[1]?.media || list[1]?.mediaUrl || ''}
                                          onChange={(e) => updateCard(1, 'media', e.target.value)}
                                          style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                                        />
                                        <label style={{ background: '#2563eb', color: '#fff', padding: '8px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                                          <Upload size={12} /> Upload
                                          <input type="file" accept="image/*" style={{ display: 'none' }}
                                            onChange={(e) => e.target.files?.[0] && handleFileUploadForItem('accessories_categories', 1, 'media', e.target.files[0])} />
                                        </label>
                                      </div>
                                      {(list[1]?.media || list[1]?.mediaUrl) && (
                                        <img src={resolveImgPreviewUrl(list[1]?.media || list[1]?.mediaUrl)} alt="Card 2" style={{ marginTop: 6, width: '100%', height: 70, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }} />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })() : (
                            sec.showBanner !== false ? (
                              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, background: '#fff', display: 'grid', gap: 14 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #cbd5e1', paddingBottom: 8 }}>
                                <div>
                                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                                    {activeMegaSubTab === 'high_silver' && 'High Silver Hover Dropdown Banner'}
                                    {activeMegaSubTab === 'atelier' && 'Atelier Hover Dropdown Banner'}
                                    {activeMegaSubTab === 'world_of_cq' && 'WORLD OF CQ Hover Dropdown Banner'}
                                  </h3>
                                  <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0 0' }}>Manage featured visual card displayed on the right side of this hover menu.</p>
                                </div>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                <div>
                                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Card Eyebrow Badge</label>
                                  <input
                                    type="text"
                                    value={sec.badgeText || ''}
                                    onChange={(e) => handleUpdateField(targetSecKey, 'badgeText', e.target.value)}
                                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 4 }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Card Headline Title</label>
                                  <input
                                    type="text"
                                    value={sec.title || ''}
                                    onChange={(e) => handleUpdateField(targetSecKey, 'title', e.target.value)}
                                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 4 }}
                                  />
                                </div>
                              </div>

                              <div>
                                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Subtitle / Narrative Description</label>
                                <textarea
                                  rows={2}
                                  value={sec.subtitle || ''}
                                  onChange={(e) => handleUpdateField(targetSecKey, 'subtitle', e.target.value)}
                                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 4 }}
                                />
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                <div>
                                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>CTA Button Text</label>
                                  <input
                                    type="text"
                                    value={sec.buttonText || ''}
                                    onChange={(e) => handleUpdateField(targetSecKey, 'buttonText', e.target.value)}
                                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 4 }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>CTA Target Link</label>
                                  <input
                                    type="text"
                                    value={sec.buttonLink || ''}
                                    onChange={(e) => handleUpdateField(targetSecKey, 'buttonLink', e.target.value)}
                                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 4 }}
                                  />
                                </div>
                              </div>

                              <div>
                                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Dropdown Cover Photo</label>
                                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                  <input
                                    type="text"
                                    value={sec.mediaUrl || ''}
                                    onChange={(e) => handleUpdateField(targetSecKey, 'mediaUrl', e.target.value)}
                                    style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                                  />
                                  <label style={{ background: '#2563eb', color: '#fff', padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Upload size={14} /> {uploadingItemKey === `${targetSecKey}_main_mediaUrl` ? 'Uploading...' : 'Upload Photo'}
                                    <input
                                      type="file"
                                      accept="image/*"
                                      disabled={uploadingItemKey === `${targetSecKey}_main_mediaUrl`}
                                      style={{ display: 'none' }}
                                      onChange={(e) => handleFileUpload(e, targetSecKey)}
                                    />
                                  </label>
                                </div>
                                {sec.mediaUrl && (
                                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <img src={resolveImgPreviewUrl(sec.mediaUrl)} alt="Preview" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }} />
                                    <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>✓ Image Active</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div style={{ background: '#fff', border: '2px dashed #cbd5e1', borderRadius: 8, padding: 18, textAlign: 'center' }}>
                              <p style={{ fontSize: 13, color: '#64748b', fontWeight: 600, margin: '0 0 10px 0' }}>
                                No featured hover banner card added for this dropdown menu.
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  handleUpdateField(targetSecKey, 'showBanner', true);
                                  handleUpdateField(targetSecKey, 'title', 'FEATURED SELECTION');
                                  handleUpdateField(targetSecKey, 'buttonText', 'EXPLORE →');
                                  handleUpdateField(targetSecKey, 'buttonLink', '/');
                                }}
                                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                              >
                                <Plus size={14} /> Add Featured Hover Banner Card
                              </button>
                            </div>
                          )
                        )
                      )}

                        {activeMegaSubTab !== 'collections' && (() => {
                          const parsedObj = (typeof sec.extraConfigParsed === 'object' && !Array.isArray(sec.extraConfigParsed)) ? sec.extraConfigParsed : {};
                          const col1Title = parsedObj.col1Title || '';
                          const col1Links = parsedObj.col1Links || [];
                          const col2Title = parsedObj.col2Title || '';
                          const col2Links = parsedObj.col2Links || [];

                          const hasCol1 = Boolean(col1Title || col1Links.length > 0);
                          const hasCol2 = Boolean(col2Title || col2Links.length > 0);

                          const PAGE_LINK_OPTIONS = [
                            { label: "Pick Existing Page Link...", value: "" },
                            { label: "All Jewellery (/jewellery)", value: "/jewellery" },
                            { label: "Rings (/jewellery?category=Rings)", value: "/jewellery?category=Rings" },
                            { label: "Necklaces (/jewellery?category=Necklaces)", value: "/jewellery?category=Necklaces" },
                            { label: "Earrings (/jewellery?category=Earrings)", value: "/jewellery?category=Earrings" },
                            { label: "Bangles & Cuffs (/jewellery?category=Bangles)", value: "/jewellery?category=Bangles" },
                            { label: "Accessories (/jewellery?category=Accessories)", value: "/jewellery?category=Accessories" },
                            { label: "Collections (/collections)", value: "/collections" },
                            { label: "Atelier & Craft (/atelier)", value: "/atelier" },
                            { label: "High Silver (/high-silver)", value: "/high-silver" },
                            { label: "About Us (/about)", value: "/about" },
                            { label: "Blog & Journal (/blog)", value: "/blog" }
                          ];

                          const updateExtraObj = (newCol1Title, newCol1Links, newCol2Title, newCol2Links) => {
                            const updatedObj = {
                              ...parsedObj,
                              col1Title: newCol1Title,
                              col1Links: newCol1Links,
                              col2Title: newCol2Title,
                              col2Links: newCol2Links
                            };
                            handleUpdateExtra(targetSecKey, updatedObj);
                          };

                          return (
                            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #cbd5e1', display: 'grid', gap: 20 }}>
                              {/* COLUMN 1 SUB-LINKS */}
                              {hasCol1 ? (
                                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                    <div style={{ flex: 1, marginRight: 12 }}>
                                      <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>Column 1 Header Title</label>
                                      <input
                                        type="text"
                                        value={col1Title}
                                        onChange={(e) => updateExtraObj(e.target.value, col1Links, col2Title, col2Links)}
                                        style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12, fontWeight: 700, marginTop: 2 }}
                                      />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newLinks = [...col1Links, { label: 'New Link', href: '/' }];
                                          updateExtraObj(col1Title, newLinks, col2Title, col2Links);
                                        }}
                                        style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                      >
                                        <Plus size={12} /> Add Link
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => updateExtraObj('', [], col2Title, col2Links)}
                                        style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                        title="Remove Column 1"
                                      >
                                        <Trash2 size={12} /> Remove Column
                                      </button>
                                    </div>
                                  </div>

                                  <div style={{ display: 'grid', gap: 6 }}>
                                    {col1Links.map((item, idx) => (
                                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.3fr 110px 30px', gap: 6, alignItems: 'center' }}>
                                        <input
                                          type="text"
                                          placeholder="Link Label"
                                          value={item.label || ''}
                                          onChange={(e) => {
                                            const newLinks = [...col1Links];
                                            newLinks[idx].label = e.target.value;
                                            updateExtraObj(col1Title, newLinks, col2Title, col2Links);
                                          }}
                                          style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                                        />
                                        <input
                                          type="text"
                                          placeholder="Target URL (e.g. /accessories)"
                                          value={item.href || ''}
                                          onChange={(e) => {
                                            const newLinks = [...col1Links];
                                            newLinks[idx].href = e.target.value;
                                            updateExtraObj(col1Title, newLinks, col2Title, col2Links);
                                          }}
                                          style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11, fontFamily: 'monospace' }}
                                        />
                                        <select
                                          value={item.href || ''}
                                          onChange={(e) => {
                                            if (e.target.value) {
                                              const newLinks = [...col1Links];
                                              newLinks[idx].href = e.target.value;
                                              updateExtraObj(col1Title, newLinks, col2Title, col2Links);
                                            }
                                          }}
                                          style={{ padding: '6px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11, background: '#f8fafc', color: '#334155' }}
                                        >
                                          {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                                          <option key={oIdx} value={opt.value}>{opt.label}</option>
                                          ))}
                                        </select>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                          <button
                                            type="button"
                                            disabled={idx === 0}
                                            onClick={() => {
                                              const newLinks = [...col1Links];
                                              const temp = newLinks[idx];
                                              newLinks[idx] = newLinks[idx - 1];
                                              newLinks[idx - 1] = temp;
                                              updateExtraObj(col1Title, newLinks, col2Title, col2Links);
                                            }}
                                            style={{ background: idx === 0 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700, cursor: idx === 0 ? 'not-allowed' : 'pointer' }}
                                          >
                                            <ArrowUp size={10} /> Up
                                          </button>
                                          <button
                                            type="button"
                                            disabled={idx === col1Links.length - 1}
                                            onClick={() => {
                                              const newLinks = [...col1Links];
                                              const temp = newLinks[idx];
                                              newLinks[idx] = newLinks[idx + 1];
                                              newLinks[idx + 1] = temp;
                                              updateExtraObj(col1Title, newLinks, col2Title, newLinks);
                                            }}
                                            style={{ background: idx === col1Links.length - 1 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700, cursor: idx === col1Links.length - 1 ? 'not-allowed' : 'pointer' }}
                                          >
                                            <ArrowDown size={10} /> Down
                                          </button>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newLinks = col1Links.filter((_, i) => i !== idx);
                                            updateExtraObj(col1Title, newLinks, col2Title, col2Links);
                                          }}
                                          style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                                          title="Delete Link"
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}

                              {/* COLUMN 2 SUB-LINKS */}
                              {hasCol2 ? (
                                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                    <div style={{ flex: 1, marginRight: 12 }}>
                                      <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>Column 2 Header Title</label>
                                      <input
                                        type="text"
                                        value={col2Title}
                                        onChange={(e) => updateExtraObj(col1Title, col1Links, e.target.value, col2Links)}
                                        style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12, fontWeight: 700, marginTop: 2 }}
                                      />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newLinks = [...col2Links, { label: 'New Link', href: '/' }];
                                          updateExtraObj(col1Title, col1Links, col2Title, newLinks);
                                        }}
                                        style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                      >
                                        <Plus size={12} /> Add Link
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => updateExtraObj(col1Title, col1Links, '', [])}
                                        style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                        title="Remove Column 2"
                                      >
                                        <Trash2 size={12} /> Remove Column
                                      </button>
                                    </div>
                                  </div>

                                  <div style={{ display: 'grid', gap: 6 }}>
                                    {col2Links.map((item, idx) => (
                                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.3fr 110px 30px', gap: 6, alignItems: 'center' }}>
                                        <input
                                          type="text"
                                          placeholder="Link Label"
                                          value={item.label || ''}
                                          onChange={(e) => {
                                            const newLinks = [...col2Links];
                                            newLinks[idx].label = e.target.value;
                                            updateExtraObj(col1Title, col1Links, col2Title, newLinks);
                                          }}
                                          style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                                        />
                                        <input
                                          type="text"
                                          placeholder="Target URL (e.g. /blog)"
                                          value={item.href || ''}
                                          onChange={(e) => {
                                            const newLinks = [...col2Links];
                                            newLinks[idx].href = e.target.value;
                                            updateExtraObj(col1Title, newLinks, col2Title, newLinks);
                                          }}
                                          style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11, fontFamily: 'monospace' }}
                                        />
                                        <select
                                          value={item.href || ''}
                                          onChange={(e) => {
                                            if (e.target.value) {
                                              const newLinks = [...col2Links];
                                              newLinks[idx].href = e.target.value;
                                              updateExtraObj(col1Title, newLinks, col2Title, newLinks);
                                            }
                                          }}
                                          style={{ padding: '6px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11, background: '#f8fafc', color: '#334155' }}
                                        >
                                          {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                                          <option key={oIdx} value={opt.value}>{opt.label}</option>
                                          ))}
                                        </select>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                          <button
                                            type="button"
                                            disabled={idx === 0}
                                            onClick={() => {
                                              const newLinks = [...col2Links];
                                              const temp = newLinks[idx];
                                              newLinks[idx] = newLinks[idx - 1];
                                              newLinks[idx - 1] = temp;
                                              updateExtraObj(col1Title, newLinks, col2Title, newLinks);
                                            }}
                                            style={{ background: idx === 0 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700, cursor: idx === 0 ? 'not-allowed' : 'pointer' }}
                                          >
                                            <ArrowUp size={10} /> Up
                                          </button>
                                          <button
                                            type="button"
                                            disabled={idx === col2Links.length - 1}
                                            onClick={() => {
                                              const newLinks = [...col2Links];
                                              const temp = newLinks[idx];
                                              newLinks[idx] = newLinks[idx + 1];
                                              newLinks[idx + 1] = temp;
                                              updateExtraObj(col1Title, newLinks, col2Title, newLinks);
                                            }}
                                            style={{ background: idx === col2Links.length - 1 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700, cursor: idx === col2Links.length - 1 ? 'not-allowed' : 'pointer' }}
                                          >
                                            <ArrowDown size={10} /> Down
                                          </button>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newLinks = col2Links.filter((_, i) => i !== idx);
                                            updateExtraObj(col1Title, newLinks, col2Title, newLinks);
                                          }}
                                          style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                                          title="Delete Link"
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}

                              {/* ADD COLUMN BUTTONS IF EMPTY */}
                              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                                {!hasCol1 && (
                                  <button
                                    type="button"
                                    onClick={() => updateExtraObj('CATEGORIES', [{ label: 'New Link', href: '/' }], col2Title, col2Links)}
                                    style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                  >
                                    <Plus size={14} /> Add Column 1
                                  </button>
                                )}
                                {!hasCol2 && (
                                  <button
                                    type="button"
                                    onClick={() => updateExtraObj(col1Title, col1Links, 'EXPLORE', [{ label: 'New Link', href: '/' }])}
                                    style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                  >
                                    <Plus size={14} /> Add Column 2
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {activeMegaSubTab === 'collections' && (
                          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #cbd5e1' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                              <div>
                                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>Featured Collections Hover Cards</h4>
                                <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0 0' }}>Manage the collection cards displayed inside the COLLECTIONS hover menu.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const list = Array.isArray(sec.extraConfigParsed) ? sec.extraConfigParsed : [];
                                  const newItem = {
                                    slug: `col_${Date.now()}`,
                                    name: "New Collection",
                                    tagline: "FINE STERLING SILVER",
                                    mediaUrl: "/images/editorial/collection-heritage.jpg",
                                    href: "/collections"
                                  };
                                  handleUpdateExtra(targetSecKey, [...list, newItem]);
                                }}
                                style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                              >
                                <Plus size={12} /> Add Collection Card
                              </button>
                            </div>

                            <div style={{ display: 'grid', gap: 10 }}>
                              {(Array.isArray(sec.extraConfigParsed) ? sec.extraConfigParsed : []).map((col, idx, arr) => (
                                <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, background: '#fff', display: 'grid', gap: 8 }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 30px', gap: 8, alignItems: 'center' }}>
                                    <input
                                      type="text"
                                      placeholder="Collection Name (e.g. HERITAGE)"
                                      value={col.name || col.label || ''}
                                      onChange={(e) => {
                                        const updated = [...arr];
                                        updated[idx].name = e.target.value;
                                        handleUpdateExtra(targetSecKey, updated);
                                      }}
                                      style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12, fontWeight: 700 }}
                                    />
                                    <input
                                      type="text"
                                      placeholder="Target Link (e.g. /collections/heritage)"
                                      value={col.href || (col.slug ? `/collections/${col.slug}` : '')}
                                      onChange={(e) => {
                                        const updated = [...arr];
                                        updated[idx].href = e.target.value;
                                        handleUpdateExtra(targetSecKey, updated);
                                      }}
                                      style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12 }}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <button
                                        type="button"
                                        disabled={idx === 0}
                                        onClick={() => handleMoveExtraItem(targetSecKey, idx, -1)}
                                        style={{ background: idx === 0 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700, cursor: idx === 0 ? 'not-allowed' : 'pointer' }}
                                      >
                                        <ArrowUp size={10} /> Up
                                      </button>
                                      <button
                                        type="button"
                                        disabled={idx === arr.length - 1}
                                        onClick={() => handleMoveExtraItem(targetSecKey, idx, 1)}
                                        style={{ background: idx === arr.length - 1 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700, cursor: idx === arr.length - 1 ? 'not-allowed' : 'pointer' }}
                                      >
                                        <ArrowDown size={10} /> Down
                                      </button>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updated = arr.filter((_, i) => i !== idx);
                                        handleUpdateExtra(targetSecKey, updated);
                                      }}
                                      style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                                      title="Delete Card"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>

                                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <input
                                      type="text"
                                      placeholder="Image URL (e.g. /images/editorial/collection-heritage.jpg)"
                                      value={col.mediaUrl || col.image || ''}
                                      onChange={(e) => {
                                        const updated = [...arr];
                                        updated[idx].mediaUrl = e.target.value;
                                        handleUpdateExtra(targetSecKey, updated);
                                      }}
                                      style={{ flex: 1, padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                                    />
                                    <label style={{
                                      background: uploadingItemKey === `${targetSecKey}_${idx}_mediaUrl` ? '#94a3b8' : '#2563eb',
                                      color: '#fff', padding: '6px 10px', borderRadius: 4,
                                      cursor: uploadingItemKey === `${targetSecKey}_${idx}_mediaUrl` ? 'not-allowed' : 'pointer',
                                      fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4
                                    }}>
                                      <Upload size={12} /> {uploadingItemKey === `${targetSecKey}_${idx}_mediaUrl` ? 'Uploading...' : 'Upload Photo'}
                                      <input
                                        type="file"
                                        accept="image/*"
                                        disabled={uploadingItemKey === `${targetSecKey}_${idx}_mediaUrl`}
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleFileUpload(e, targetSecKey, idx, 'mediaUrl')}
                                      />
                                    </label>
                                    {(col.mediaUrl || col.image) && (
                                      <img src={resolveImgPreviewUrl(col.mediaUrl || col.image)} alt="Preview" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid #cbd5e1' }} />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* TAB: Collections Page Grid Cards Editor */}
          {activeTab === 'page_collections' && (
            <div style={{ display: 'grid', gap: 24 }}>
              {/* Header Banner Settings */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 12px 0'}}>Collections Page Header Intro</h3>
                <div style={{ display: 'grid', gap: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Eyebrow Badge</label>
                      <input
                        type="text"
                        value={currentSection.badgeText || 'The house'}
                        onChange={(e) => handleUpdateField('page_collections', 'badgeText', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Main Page Title</label>
                      <input
                        type="text"
                        value={currentSection.title || 'Collections'}
                        onChange={(e) => handleUpdateField('page_collections', 'title', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Page Intro Story Narrative</label>
                    <textarea
                      rows={2}
                      value={currentSection.subtitle || 'Each collection is a way of working rather than a season.'}
                      onChange={(e) => handleUpdateField('page_collections', 'subtitle', e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Banner CTA Button Text</label>
                      <input
                        type="text"
                        placeholder="e.g. Explore All Pieces"
                        value={currentSection.buttonText || ''}
                        onChange={(e) => handleUpdateField('page_collections', 'buttonText', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Banner CTA Button Link</label>
                      <input
                        type="text"
                        placeholder="e.g. /jewellery or /collections"
                        value={currentSection.buttonLink || ''}
                        onChange={(e) => handleUpdateField('page_collections', 'buttonLink', e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                      />
                      <select
                        value={currentSection.buttonLink || ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleUpdateField('page_collections', 'buttonLink', e.target.value);
                          }
                        }}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 11, background: '#f8fafc', color: '#334155', marginTop: 4 }}
                      >
                        {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                          <option key={oIdx} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Collections Cards Array */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>House Collections Grid Showcase</h3>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0 0' }}>Manage cover photos, taglines, stories, and order of all collection cards.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const list = asList(currentSection.extraConfigParsed);
                      const num = (list.length + 1);
                      const newItem = {
                        slug: `collection-${num}`,
                        name: `Collection ${num}`,
                        tagline: "FINE 925 STERLING SILVER",
                        story: "Handcrafted sterling silver pieces designed to be worn and remembered.",
                        mediaUrl: "/images/editorial/collection-heritage.jpg",
                        pieces: "",
                        startingPrice: "",
                        isActive: true,
                        buttonText: "Explore",
                        href: ""
                      };
                      handleUpdateExtra('page_collections', [...list, newItem]);
                    }}
                    style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Plus size={14} /> Add Collection Card
                  </button>
                </div>

                <div style={{ display: 'grid', gap: 16 }}>
                  {(Array.isArray(currentSection.extraConfigParsed) ? currentSection.extraConfigParsed : []).map((col, idx) => (
                    <div key={idx} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: col.isActive !== false ? '#2563eb' : '#64748b' }}>
                          Collection Card #{idx + 1} — {col.name || 'Untitled'} {col.isActive === false && <span style={{ color: '#ef4444', fontSize: 11 }}>(Hidden)</span>}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: col.isActive !== false ? '#16a34a' : '#64748b', cursor: 'pointer', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '3px 8px' }}>
                            <input
                              type="checkbox"
                              checked={col.isActive !== false}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].isActive = e.target.checked;
                                handleUpdateExtra('page_collections', updated);
                              }}
                            />
                            {col.isActive !== false ? 'Active' : 'Hidden'}
                          </label>
                          <button
                            type="button"
                            disabled={idx === 0}
                            onClick={() => handleMoveExtraItem('page_collections', idx, -1)}
                            style={{ background: idx === 0 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: idx === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                          >
                            <ArrowUp size={12} /> Move Up
                          </button>
                          <button
                            type="button"
                            disabled={idx === (currentSection.extraConfigParsed?.length || 1) - 1}
                            onClick={() => handleMoveExtraItem('page_collections', idx, 1)}
                            style={{ background: idx === (currentSection.extraConfigParsed?.length || 1) - 1 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: idx === (currentSection.extraConfigParsed?.length || 1) - 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                          >
                            <ArrowDown size={12} /> Move Down
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (asList(currentSection.extraConfigParsed)).filter((_, i) => i !== idx);
                              handleUpdateExtra('page_collections', updated);
                            }}
                            style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer', marginLeft: 8 }}
                            title="Delete Card"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gap: 12 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: 10 }}>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Collection Name</label>
                            <input
                              type="text"
                              value={col.name || ''}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                const newName = e.target.value;
                                updated[idx].name = newName;
                                if (!updated[idx].slug || String(updated[idx].slug).startsWith('new_col_')) {
                                  updated[idx].slug = newName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
                                }
                                handleUpdateExtra('page_collections', updated);
                              }}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Slug / URL Key</label>
                            <input
                              type="text"
                              placeholder="e.g. heritage"
                              value={col.slug || ''}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].slug = e.target.value.toLowerCase().replace(/[^a-z0-9_-]+/g, '-');
                                handleUpdateExtra('page_collections', updated);
                              }}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Tagline Badge</label>
                            <input
                              type="text"
                              value={col.tagline || ''}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].tagline = e.target.value;
                                handleUpdateExtra('page_collections', updated);
                              }}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Starting Price / Count</label>
                            <input
                              type="text"
                              placeholder="e.g. from ₹3,500"
                              value={col.startingPrice || ''}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].startingPrice = e.target.value;
                                handleUpdateExtra('page_collections', updated);
                              }}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                            />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Card Button Text</label>
                            <input
                              type="text"
                              placeholder="e.g. Explore"
                              value={col.buttonText || ''}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].buttonText = e.target.value;
                                handleUpdateExtra('page_collections', updated);
                              }}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Card Target Link (URL)</label>
                            <input
                              type="text"
                              placeholder={col.slug ? `/collections/${col.slug}` : 'e.g. /collections/heritage or /jewellery'}
                              value={col.href || ''}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].href = e.target.value;
                                handleUpdateExtra('page_collections', updated);
                              }}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                            />
                          </div>
                        </div>

                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Story Narrative</label>
                          <textarea
                            rows={2}
                            value={col.story || ''}
                            onChange={(e) => {
                              const updated = [...asList(currentSection.extraConfigParsed)];
                              updated[idx].story = e.target.value;
                              handleUpdateExtra('page_collections', updated);
                            }}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 2 }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Collection Cover Photo</label>
                          <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                            <input
                              type="text"
                              value={col.mediaUrl || col.image || ''}
                              onChange={(e) => {
                                const updated = [...asList(currentSection.extraConfigParsed)];
                                updated[idx].mediaUrl = e.target.value;
                                updated[idx].image = e.target.value;
                                handleUpdateExtra('page_collections', updated);
                              }}
                              style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                            />
                            <label style={{
                              background: uploadingItemKey === `page_collections_${idx}_mediaUrl` ? '#94a3b8' : '#2563eb',
                              color: '#fff', padding: '6px 12px', borderRadius: 6,
                              cursor: uploadingItemKey === `page_collections_${idx}_mediaUrl` ? 'not-allowed' : 'pointer',
                              fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4
                            }}>
                              <Upload size={12} /> {uploadingItemKey === `page_collections_${idx}_mediaUrl` ? 'Uploading...' : 'Upload Photo'}
                              <input
                                type="file"
                                accept="image/*"
                                disabled={uploadingItemKey === `page_collections_${idx}_mediaUrl`}
                                style={{ display: 'none' }}
                                onChange={(e) => handleFileUpload(e, 'page_collections', idx, 'mediaUrl')}
                              />
                            </label>
                          </div>
                          {(col.mediaUrl || col.image) && (
                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <img src={resolveImgPreviewUrl(col.mediaUrl || col.image)} alt="Preview" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, border: '1px solid #cbd5e1' }} />
                              <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>✓ Image Active</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Generic fallback editor for other tabs */}
          {activeTab === 'published_items' && (
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#1e40af' }}>
                These are the pieces published from <b>Purchase &gt; Item View &gt; Publish to Website</b>.
                The order here is the order they appear on the website - featured pieces always come first.
                To change a photo, price or description, open the piece from the Purchase Item View.
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#94a3b8' }} />
                  <input
                    type="text"
                    placeholder="Search by title, SKU or category..."
                    value={publishedSearch}
                    onChange={(e) => setPublishedSearch(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px 9px 30px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {publishedItems.filter(i => i.showOnWebsite && !i.isSold && String(i.productStatus || '').toLowerCase() !== 'sold').length} live
                  {publishedItems.some(i => i.isSold || String(i.productStatus || '').toLowerCase() === 'sold') ? ` / ${publishedItems.filter(i => i.isSold || String(i.productStatus || '').toLowerCase() === 'sold').length} sold` : ''}
                </span>
                <button
                  type="button"
                  onClick={fetchPublishedItems}
                  style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, color: '#334155', cursor: 'pointer' }}
                >
                  Refresh
                </button>
              </div>

              {publishedLoading && (
                <div style={{ textAlign: 'center', padding: 30, color: '#64748b', fontSize: 13 }}>Loading published products...</div>
              )}

              {!publishedLoading && visiblePublishedItems.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#64748b', fontSize: 13, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10 }}>
                  <Package size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
                  <div style={{ fontWeight: 700, color: '#334155' }}>No published pieces yet</div>
                  <div style={{ marginTop: 4 }}>Go to Purchase &gt; Item View, click the globe icon on a piece and publish it.</div>
                </div>
              )}

              {!publishedLoading && visiblePublishedItems.map((item) => {
                const idx = publishedItems.indexOf(item);
                const hidden = !item.showOnWebsite;
                return (
                  <div
                    key={item.purchaseDetailID}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '64px 1fr auto',
                      gap: 14,
                      alignItems: 'center',
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      padding: 10,
                      background: hidden ? '#f8fafc' : '#fff',
                      opacity: hidden || item.isSold ? 0.62 : 1
                    }}
                  >
                    <div style={{ width: 64, height: 64, borderRadius: 8, overflow: 'hidden', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {item.primaryImage ? (
                        <img src={resolveItemImage(item.primaryImage)} alt={item.webTitle} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <ImageIcon size={18} color="#475569" />
                      )}
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{item.webTitle}</span>
                        {item.isFeatured && (
                          <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 20, padding: '1px 8px', fontSize: 10, fontWeight: 800 }}>FEATURED</span>
                        )}
                        {item.homeSection && (
                          <span style={{ background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 20, padding: '1px 8px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>
                            {homeCarouselLabel(item.homeSection)}
                          </span>
                        )}
                        {(item.isSold || String(item.productStatus || '').toLowerCase() === 'sold') ? (
                          <span style={{ background: '#ef4444', color: '#fff', border: '1px solid #dc2626', borderRadius: 20, padding: '2px 10px', fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
                            SOLD OUT
                          </span>
                        ) : null}
                        {hidden && (
                          <span style={{ background: '#e2e8f0', color: '#475569', borderRadius: 20, padding: '1px 8px', fontSize: 10, fontWeight: 800 }}>HIDDEN</span>
                        )}
                        {item.imageCount === 0 && (
                          <span style={{ background: '#ffedd5', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 20, padding: '1px 8px', fontSize: 10, fontWeight: 800 }}>NO PHOTOS</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3 }}>
                        {item.skuNo} · {item.category || 'Uncategorised'} · {item.gender}
                        {item.tileSize && item.tileSize !== 'normal' ? ` · ${item.tileSize} tile` : ''}
                        {item.imageCount > 0 ? ` · ${item.imageCount} photo${item.imageCount > 1 ? 's' : ''}` : ''}
                      </div>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a', marginTop: 3 }}>
                        ₹{Number(item.displayPrice || 0).toLocaleString('en-IN')}
                      </div>
                    </div>

                    {/* Every action is the same size and squarely centred, and the two
                        that carried no colour now read as clearly as the rest. At 13px
                        in three shades of grey these were unreadable, and nobody could
                        tell the edit button from the open-the-website one. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <button
                        type="button"
                        title="Move up"
                        onClick={() => movePublishedItem(idx, -1)}
                        style={{ ...rowAction, color: '#475569' }}
                      >
                        <ArrowUp size={15} />
                      </button>
                      <button
                        type="button"
                        title="Move down"
                        onClick={() => movePublishedItem(idx, 1)}
                        style={{ ...rowAction, color: '#475569' }}
                      >
                        <ArrowDown size={15} />
                      </button>

                      <span style={rowDivider} />

                      <button
                        type="button"
                        title={item.isFeatured ? 'Remove from featured' : 'Mark as featured'}
                        onClick={() => toggleItemFeatured(idx)}
                        style={{
                          ...rowAction,
                          background: item.isFeatured ? '#fef3c7' : '#fff',
                          borderColor: item.isFeatured ? '#f59e0b' : '#cbd5e1',
                          color: item.isFeatured ? '#b45309' : '#64748b',
                        }}
                      >
                        <Star size={15} fill={item.isFeatured ? '#f59e0b' : 'none'} />
                      </button>
                      <button
                        type="button"
                        title={hidden ? 'Show on website' : 'Hide from website'}
                        onClick={() => toggleItemVisibility(item)}
                        style={{
                          ...rowAction,
                          background: hidden ? '#f1f5f9' : '#fff',
                          color: hidden ? '#94a3b8' : '#16a34a',
                        }}
                      >
                        {hidden ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>

                      <span style={rowDivider} />

                      <button
                        type="button"
                        title="Edit website details"
                        onClick={() => { setOpenRowMenu(null); setEditItem(item); }}
                        style={{ ...rowAction, color: '#334155' }}
                      >
                        <Pencil size={15} />
                      </button>
                      <a
                        href={`${STOREFRONT}/products/${item.seoSlug}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Open on the website"
                        style={{ ...rowAction, color: '#2563eb', textDecoration: 'none' }}
                      >
                        <ExternalLink size={15} />
                      </a>

                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          title="More options"
                          onClick={() => setOpenRowMenu(openRowMenu === item.purchaseDetailID ? null : item.purchaseDetailID)}
                          style={{ background: openRowMenu === item.purchaseDetailID ? '#e2e8f0' : '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 5px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                          <MoreVertical size={13} color="#475569" />
                        </button>

                        {openRowMenu === item.purchaseDetailID && (
                          <>
                            {/* Click anywhere else to close the menu */}
                            <div onClick={() => setOpenRowMenu(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 40 }} />
                            <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50, width: 246, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 12px 30px rgba(15,23,42,0.18)', padding: 6 }}>
                              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: '#94a3b8', padding: '6px 8px 4px' }}>SHOW ON HOME PAGE</div>
                              {HOME_CAROUSEL_OPTIONS.map((opt) => {
                                const active = item.homeSection === opt.value;
                                return (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => changeItemHomeSection(item, opt.value)}
                                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: active ? '#eff6ff' : 'transparent', border: 'none', borderRadius: 7, padding: 8, fontSize: 12.5, fontWeight: active ? 700 : 600, color: active ? '#1d4ed8' : '#334155', cursor: 'pointer', textAlign: 'left' }}
                                  >
                                    <Check size={13} style={{ flexShrink: 0, opacity: active ? 1 : 0 }} />
                                    {opt.label}
                                  </button>
                                );
                              })}
                              <div style={{ height: 1, background: '#e2e8f0', margin: '5px 4px' }} />
                              <button
                                type="button"
                                disabled={!item.homeSection}
                                onClick={() => changeItemHomeSection(item, '')}
                                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', borderRadius: 7, padding: 8, fontSize: 12.5, fontWeight: 600, color: item.homeSection ? '#b91c1c' : '#cbd5e1', cursor: item.homeSection ? 'pointer' : 'not-allowed', textAlign: 'left' }}
                              >
                                <XCircle size={13} style={{ flexShrink: 0 }} />
                                Remove from home page
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {publishedDirty && (
                <div style={{ position: 'sticky', bottom: 0, background: '#0f172a', color: '#fff', borderRadius: 10, padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12.5, fontWeight: 700 }}>
                  <span>Order changed - not saved yet</span>
                  <button
                    type="button"
                    onClick={savePublishedOrder}
                    disabled={savingOrder}
                    style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                  >
                    {savingOrder ? 'Saving...' : 'Save Running Order'}
                  </button>
                </div>
              )}

              {editItem && (
                <PublishItemModal
                  piece={{ ...editItem, itemName: editItem.webTitle, salePrice: editItem.displayPrice }}
                  onClose={() => setEditItem(null)}
                  onSaved={handleEditSaved}
                />
              )}
            </div>
          )}

          {activeTab === 'grid_banners' && (
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#1e40af' }}>
                These wide campaign tiles sit inside the product grid on the Jewellery, Accessories and Collection pages.
                <b> Show after</b> counts products: <b>0</b> puts the banner before the first product,
                <b>4</b> puts it after the fourth. Each banner is two tiles wide.
              </div>

              {(asList(currentSection.extraConfigParsed)).map((banner, idx) => (
                <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, background: '#f8fafc' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a' }}>
                      Banner {idx + 1} {banner.title ? `- ${banner.title}` : ''}
                    </span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => handleMoveExtraItem(activeTab, idx, -1)}
                        style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}
                      >
                        <ArrowUp size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveExtraItem(activeTab, idx, 1)}
                        style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}
                      >
                        <ArrowDown size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (asList(currentSection.extraConfigParsed)).filter((_, i) => i !== idx);
                          handleUpdateExtra(activeTab, updated);
                        }}
                        style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '130px minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Show after</label>
                      <input
                        type="number"
                        min={0}
                        value={banner.position ?? 0}
                        onChange={(e) => {
                          const updated = [...(asList(currentSection.extraConfigParsed))];
                          updated[idx] = { ...updated[idx], position: Math.max(0, Number(e.target.value) || 0) };
                          handleUpdateExtra(activeTab, updated);
                        }}
                        style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                      />
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, lineHeight: 1.35 }}>
                        {Number(banner.position ?? 0) === 0
                          ? 'Before the first product'
                          : `After ${banner.position} product${Number(banner.position) === 1 ? '' : 's'}`}
                      </div>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Badge / Eyebrow</label>
                      <input
                        type="text"
                        value={banner.badge || ''}
                        onChange={(e) => {
                          const updated = [...(asList(currentSection.extraConfigParsed))];
                          updated[idx] = { ...updated[idx], badge: e.target.value };
                          handleUpdateExtra(activeTab, updated);
                        }}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                      />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Banner Title</label>
                      <input
                        type="text"
                        value={banner.title || ''}
                        onChange={(e) => {
                          const updated = [...(asList(currentSection.extraConfigParsed))];
                          updated[idx] = { ...updated[idx], title: e.target.value };
                          handleUpdateExtra(activeTab, updated);
                        }}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Button Text</label>
                      <input
                        type="text"
                        value={banner.buttonText || ''}
                        onChange={(e) => {
                          const updated = [...(asList(currentSection.extraConfigParsed))];
                          updated[idx] = { ...updated[idx], buttonText: e.target.value };
                          handleUpdateExtra(activeTab, updated);
                        }}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Button Link</label>
                      <select
                        value={banner.buttonLink || ''}
                        onChange={(e) => {
                          const updated = [...(asList(currentSection.extraConfigParsed))];
                          updated[idx] = { ...updated[idx], buttonLink: e.target.value };
                          handleUpdateExtra(activeTab, updated);
                        }}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 4, background: '#fff' }}
                      >
                        {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                        <option key={oIdx} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px', gap: 10, marginTop: 10, alignItems: 'end' }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Banner Photo</label>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <input
                          type="text"
                          value={banner.mediaUrl || ''}
                          onChange={(e) => {
                            const updated = [...(asList(currentSection.extraConfigParsed))];
                            updated[idx] = { ...updated[idx], mediaUrl: e.target.value };
                            handleUpdateExtra(activeTab, updated);
                          }}
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12.5 }}
                        />
                        <label style={{ background: '#2563eb', color: '#fff', padding: '8px 12px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Upload size={13} /> Upload
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => e.target.files?.[0] && handleFileUploadForItem(activeTab, idx, 'mediaUrl', e.target.files[0])}
                          />
                        </label>
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Backdrop</label>
                      <input
                        type="color"
                        value={banner.bgColor || '#1C1816'}
                        onChange={(e) => {
                          const updated = [...(asList(currentSection.extraConfigParsed))];
                          updated[idx] = { ...updated[idx], bgColor: e.target.value };
                          handleUpdateExtra(activeTab, updated);
                        }}
                        style={{ width: '100%', height: 36, borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 4, cursor: 'pointer' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Badge Colour</label>
                      <input
                        type="color"
                        value={banner.accentColor || '#ffffff'}
                        onChange={(e) => {
                          const updated = [...(asList(currentSection.extraConfigParsed))];
                          updated[idx] = { ...updated[idx], accentColor: e.target.value };
                          handleUpdateExtra(activeTab, updated);
                        }}
                        style={{ width: '100%', height: 36, borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 4, cursor: 'pointer' }}
                      />
                    </div>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>
                    <input
                      type="checkbox"
                      checked={banner.isActive !== false}
                      onChange={(e) => {
                        const updated = [...(asList(currentSection.extraConfigParsed))];
                        updated[idx] = { ...updated[idx], isActive: e.target.checked };
                        handleUpdateExtra(activeTab, updated);
                      }}
                      style={{ width: 16, height: 16 }}
                    />
                    <span>{banner.isActive !== false ? 'Live on website' : 'Hidden'}</span>
                  </label>
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  const list = asList(currentSection.extraConfigParsed);
                  handleUpdateExtra(activeTab, [...list, {
                    position: list.length ? Number(list[list.length - 1].position || 0) + 6 : 2,
                    badge: 'New Campaign',
                    title: 'Campaign Headline',
                    buttonText: 'Explore',
                    buttonLink: '/jewellery',
                    mediaUrl: '',
                    bgColor: '#1C1816',
                    accentColor: '#ffffff',
                    isActive: true
                  }]);
                }}
                style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, justifySelf: 'start' }}
              >
                <Plus size={14} /> Add Campaign Banner
              </button>
            </div>
          )}

          {['promo_popup', 'value_props', 'brand_story', 'editorial_banner_1', 'editorial_banner_2', 'gifting_banner', 'newsletter_banner', 'jewellery_seo_block', 'page_jewellery', 'page_accessories', 'page_atelier', 'page_blog', 'page_about', 'high_silver_hero', 'high_silver_categories', 'high_silver_collection', 'high_silver_appointment', 'about_banner', 'about_contact', 'about_founder', 'atelier_process', 'atelier_artisans', 'atelier_salon'].includes(activeTab) && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Badge / Eyebrow Text</label>
                <input
                  type="text"
                  value={currentSection.badgeText || ''}
                  onChange={(e) => handleUpdateField(activeTab, 'badgeText', e.target.value)}
                  placeholder="e.g. The Atelier Collection"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Section Title / Headline</label>
                <input
                  type="text"
                  value={currentSection.title || ''}
                  onChange={(e) => handleUpdateField(activeTab, 'title', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Subtitle / Narrative Description</label>
                <textarea
                  rows={3}
                  value={currentSection.subtitle || ''}
                  onChange={(e) => handleUpdateField(activeTab, 'subtitle', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>
              {!hasOwnButtonList(currentSection) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Button Text</label>
                  <input
                    type="text"
                    value={currentSection.buttonText || ''}
                    onChange={(e) => handleUpdateField(activeTab, 'buttonText', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Button Target Link</label>
                  <input
                    type="text"
                    value={currentSection.buttonLink || ''}
                    onChange={(e) => handleUpdateField(activeTab, 'buttonLink', e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                  />
                  <select
                    value={currentSection.buttonLink || ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        handleUpdateField(activeTab, 'buttonLink', e.target.value);
                      }
                    }}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 11, background: '#f8fafc', color: '#334155', marginTop: 4 }}
                  >
                    {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                    <option key={oIdx} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              )}

              {/* Extra buttons — the gifting banner carries a row of them rather
                  than the single call to action the other sections use. They live
                  inside the section's object alongside whatever else is stored
                  there, so editing them here cannot quietly drop the rest. */}
              {activeTab === 'gifting_banner' && (() => {
                const stored = currentSection.extraConfigParsed;
                const buttons = Array.isArray(stored)
                  ? stored
                  : (stored && typeof stored === 'object' && Array.isArray(stored.buttons))
                    ? stored.buttons
                    : [];
                const extra = (stored && !Array.isArray(stored) && typeof stored === 'object') ? stored : {};
                const update = (next) => handleUpdateExtra('gifting_banner', { ...extra, buttons: next });

                // Three buttons were configured here long ago under a naming the
                // website never read. Rather than leave them stranded, offer them.
                const legacy = [1, 2, 3]
                  .map((n) => ({ text: extra['btn' + n + 'Text'], link: extra['btn' + n + 'Link'], style: n === 1 ? 'solid' : n === 2 ? 'outline' : 'link' }))
                  .filter((b) => b.text && b.link);

                const move = (idx, direction) => {
                  const t = idx + direction;
                  if (t < 0 || t >= buttons.length) return;
                  const next = [...buttons];
                  next[idx] = buttons[t];
                  next[t] = buttons[idx];
                  update(next);
                };

                return (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, background: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Banner Buttons</label>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => update([...buttons, { text: 'NEW BUTTON', link: '/gifts', style: 'outline' }])}
                      >
                        <Plus size={13} /> Add Button
                      </button>
                    </div>

                    <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 12px', lineHeight: 1.6 }}>
                      {buttons.length === 0
                        ? 'Empty — the banner is showing its three built-in buttons (Gifts For Her, Gifts For Him, and the one above). Add a button here and this list replaces all three.'
                        : 'This list is what the banner shows, in this order.'}
                    </p>

                    {buttons.length === 0 && legacy.length > 0 && (
                      <div style={{ marginBottom: 12, padding: '8px 10px', borderRadius: 6, background: '#fffbeb', border: '1px solid #fde68a' }}>
                        <p style={{ fontSize: 11, color: '#92400e', margin: '0 0 6px', lineHeight: 1.6 }}>
                          {legacy.length} button{legacy.length > 1 ? 's were' : ' was'} saved here earlier but the website was never showing {legacy.length > 1 ? 'them' : 'it'}: {legacy.map((b) => b.text).join(', ')}.
                        </p>
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => update(legacy)}>
                          Use these
                        </button>
                      </div>
                    )}

                    <div style={{ display: 'grid', gap: 10 }}>
                      {buttons.map((btn, idx) => (
                        <div key={idx} style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: 10, background: '#fff', display: 'grid', gap: 8 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 130px', gap: 8 }}>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Button Text</label>
                              <input
                                type="text"
                                value={btn.text || ''}
                                onChange={(e) => {
                                  const next = [...buttons];
                                  next[idx] = { ...next[idx], text: e.target.value };
                                  update(next);
                                }}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Target Link</label>
                              <input
                                type="text"
                                value={btn.link || ''}
                                onChange={(e) => {
                                  const next = [...buttons];
                                  next[idx] = { ...next[idx], link: e.target.value };
                                  update(next);
                                }}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Look</label>
                              <select
                                value={btn.style || 'solid'}
                                onChange={(e) => {
                                  const next = [...buttons];
                                  next[idx] = { ...next[idx], style: e.target.value };
                                  update(next);
                                }}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
                              >
                                <option value="solid">Filled (white)</option>
                                <option value="outline">Outlined</option>
                                <option value="link">Plain link</option>
                              </select>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <select
                              value=""
                              onChange={(e) => {
                                if (!e.target.value) return;
                                const next = [...buttons];
                                next[idx] = { ...next[idx], link: e.target.value };
                                update(next);
                              }}
                              style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 11, background: '#f8fafc', color: '#334155' }}
                            >
                              {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                                <option key={oIdx} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <button type="button" className="btn btn-sm btn-ghost" disabled={idx === 0} onClick={() => move(idx, -1)}>
                              <ArrowUp size={13} />
                            </button>
                            <button type="button" className="btn btn-sm btn-ghost" disabled={idx === buttons.length - 1} onClick={() => move(idx, 1)}>
                              <ArrowDown size={13} />
                            </button>
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => update(buttons.filter((_, i) => i !== idx))}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Section Cover Photo / Media</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <input
                    type="text"
                    value={currentSection.mediaUrl || ''}
                    onChange={(e) => handleUpdateField(activeTab, 'mediaUrl', e.target.value)}
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                  <label style={{ background: '#2563eb', color: '#fff', padding: '10px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Upload size={14} /> Upload Photo
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => e.target.files?.[0] && handleMediaUpload(activeTab, e.target.files[0])}
                    />
                  </label>
                </div>
                {currentSection.mediaUrl && (
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src={resolveImgPreviewUrl(currentSection.mediaUrl)} alt="Preview" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }} />
                    <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>✓ Image Active</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {activeTab === 'accessories_categories' && (
            <div style={{ display: 'grid', gap: 10, marginTop: 18, paddingTop: 18, borderTop: '1px dashed #cbd5e1' }}>
              <label style={{ fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase' }}>
                Categories this page carries
              </label>
              <div style={{ fontSize: 11.5, color: '#64748b' }}>
                Only pieces in the ticked categories show on the Accessories page. Untick one and it drops off.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {ACCESSORY_CATEGORY_OPTIONS.map((name) => {
                  const list = Array.isArray(currentSection.extraConfigParsed) ? currentSection.extraConfigParsed : [];
                  const on = list.some((c) => String(c.name || '').toLowerCase() === name.toLowerCase());
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => handleUpdateExtra('accessories_categories', on
                        ? list.filter((c) => String(c.name || '').toLowerCase() !== name.toLowerCase())
                        : [...list, { name }])}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                        background: on ? '#eff6ff' : '#fff',
                        border: '1px solid ' + (on ? '#93c5fd' : '#cbd5e1'),
                        color: on ? '#1d4ed8' : '#475569',
                        borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 700
                      }}
                    >
                      {on ? <Check size={12} /> : <Plus size={12} />} {name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'high_silver_hero' && (
            <div style={{ display: 'grid', gap: 12, marginTop: 18, paddingTop: 18, borderTop: '1px dashed #cbd5e1' }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase' }}>
                  Caption under the hero photo
                </label>
                <input
                  type="text"
                  placeholder="e.g. Stars & Mosaic Collar Necklace in solid 925 sterling silver..."
                  value={(!Array.isArray(currentSection.extraConfigParsed) && currentSection.extraConfigParsed?.caption) || ''}
                  onChange={(e) => {
                    const prev = (!Array.isArray(currentSection.extraConfigParsed) && currentSection.extraConfigParsed) || {};
                    handleUpdateExtra('high_silver_hero', { ...prev, caption: e.target.value });
                  }}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase' }}>
                  Text Position On Banner
                </label>
                <select
                  value={(!Array.isArray(currentSection.extraConfigParsed) && currentSection.extraConfigParsed?.align) || 'mt'}
                  onChange={(e) => {
                    const prev = (!Array.isArray(currentSection.extraConfigParsed) && currentSection.extraConfigParsed) || {};
                    handleUpdateExtra('high_silver_hero', { ...prev, align: e.target.value });
                  }}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', marginTop: 4 }}
                >
                  <option value="mt">mt — Top Center (Middle Top)</option>
                  <option value="ml">ml — Middle Left</option>
                  <option value="center">center — Middle Center</option>
                  <option value="bl">bl — Bottom Left</option>
                  <option value="br">br — Bottom Right</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'high_silver_chapters' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  Chapter Banners ({(asList(currentSection.extraConfigParsed)).length})
                </h3>
                <button
                  type="button"
                  onClick={() => handleUpdateExtra('high_silver_chapters', [
                    ...(asList(currentSection.extraConfigParsed)),
                    {
                      id: Date.now(),
                      title: 'New Chapter',
                      description: '',
                      buttonText: 'Discover The Collections',
                      buttonLink: '/collections',
                      mediaUrl: '',
                      overlayText: '',
                      captionTitle: '',
                      captionSubtitle: '',
                      background: '#FDFCFA',
                      align: 'top',
                      captionTone: 'light',
                      isActive: true
                    }
                  ])}
                  style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Plus size={13} /> Add Chapter
                </button>
              </div>

              {(asList(currentSection.extraConfigParsed)).map((chapter, idx) => {
                const list = asList(currentSection.extraConfigParsed);
                const patch = (field, value) => {
                  const updated = [...list];
                  updated[idx] = { ...updated[idx], [field]: value };
                  handleUpdateExtra('high_silver_chapters', updated);
                };
                const move = (dir) => {
                  const target = idx + dir;
                  if (target < 0 || target >= list.length) return;
                  const updated = [...list];
                  [updated[idx], updated[target]] = [updated[target], updated[idx]];
                  handleUpdateExtra('high_silver_chapters', updated);
                };
                return (
                  <div key={chapter.id ?? idx} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, background: '#f8fafc', display: 'grid', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>Chapter {idx + 1}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, color: chapter.isActive === false ? '#94a3b8' : '#16a34a', cursor: 'pointer' }}>
                          <input type="checkbox" checked={chapter.isActive !== false} onChange={(e) => patch('isActive', e.target.checked)} />
                          {chapter.isActive === false ? 'Hidden' : 'Live'}
                        </label>
                        <button type="button" onClick={() => move(-1)} disabled={idx === 0}
                          style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}>
                          <ArrowUp size={13} />
                        </button>
                        <button type="button" onClick={() => move(1)} disabled={idx === list.length - 1}
                          style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === list.length - 1 ? 'not-allowed' : 'pointer' }}>
                          <ArrowDown size={13} />
                        </button>
                        <button type="button" onClick={() => handleUpdateExtra('high_silver_chapters', list.filter((_, i) => i !== idx))}
                          style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Chapter Title</label>
                        <input type="text" value={chapter.title || ''} onChange={(e) => patch('title', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Button Text</label>
                        <input type="text" value={chapter.buttonText || ''} onChange={(e) => patch('buttonText', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Intro Paragraph (under the title)</label>
                      <textarea rows={2} value={chapter.description || ''} onChange={(e) => patch('description', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3, fontFamily: 'inherit' }} />
                    </div>

                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Button Link</label>
                      <select value={chapter.buttonLink || ''} onChange={(e) => patch('buttonLink', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, background: '#fff', marginTop: 3 }}>
                        {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                          <option key={oIdx} value={opt.value}>{opt.label}</option>
                        ))}
                        {chapter.buttonLink && !PAGE_LINK_OPTIONS.some((o) => o.value === chapter.buttonLink) && (
                          <option value={chapter.buttonLink}>{chapter.buttonLink}</option>
                        )}
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Banner Photo</label>
                      <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                        <input type="text" value={chapter.mediaUrl || ''} onChange={(e) => patch('mediaUrl', e.target.value)}
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }} />
                        <label style={{ background: '#2563eb', color: '#fff', padding: '8px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Upload size={12} /> Upload
                          <input type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={(e) => e.target.files?.[0] && handleFileUploadForItem('high_silver_chapters', idx, 'mediaUrl', e.target.files[0])} />
                        </label>
                      </div>
                      {chapter.mediaUrl && (
                        <img src={resolveImgPreviewUrl(chapter.mediaUrl)} alt="Chapter" style={{ marginTop: 6, width: 64, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }} />
                      )}
                    </div>

                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Text On The Photo</label>
                      <textarea rows={3} value={chapter.overlayText || ''} onChange={(e) => patch('overlayText', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3, fontFamily: 'inherit' }} />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Piece Name (small print)</label>
                        <input type="text" value={chapter.captionTitle || ''} onChange={(e) => patch('captionTitle', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Piece Details (small print)</label>
                        <input type="text" value={chapter.captionSubtitle || ''} onChange={(e) => patch('captionSubtitle', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Section Background</label>
                        <input type="color" value={chapter.background || '#FDFCFA'} onChange={(e) => patch('background', e.target.value)}
                          style={{ width: '100%', height: 36, borderRadius: 6, border: '1px solid #cbd5e1', cursor: 'pointer', marginTop: 3 }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Text Position</label>
                        <select value={chapter.align || 'bl'} onChange={(e) => patch('align', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, background: '#fff', marginTop: 3 }}>
                          <option value="mt">mt — Top Center (Middle Top)</option>
                          <option value="ml">ml — Middle Left</option>
                          <option value="center">center — Middle Center</option>
                          <option value="bl">bl — Bottom Left</option>
                          <option value="br">br — Bottom Right</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Small Print Colour</label>
                        <select value={chapter.captionTone || 'light'} onChange={(e) => patch('captionTone', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, background: '#fff', marginTop: 3 }}>
                          <option value="light">White (dark photo)</option>
                          <option value="dark">Black (light photo)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}

              {(asList(currentSection.extraConfigParsed)).length === 0 && (
                <div style={{ textAlign: 'center', padding: 30, color: '#64748b', fontSize: 12.5, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10 }}>
                  No chapters yet - the High Silver page will show only the hero and the appointment band.
                </div>
              )}
            </div>
          )}

          {activeTab === 'high_silver_categories' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  Category Bar Cards ({(asList(currentSection.extraConfigParsed)).length})
                </h3>
                <button
                  type="button"
                  onClick={() => handleUpdateExtra('high_silver_categories', [
                    ...(asList(currentSection.extraConfigParsed)),
                    {
                      id: Date.now(),
                      label: 'New Category',
                      media: '/images/editorial/category-necklaces.jpg',
                      link: '/jewellery?category=Necklaces'
                    }
                  ])}
                  style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Plus size={13} /> Add Category Card
                </button>
              </div>

              {(asList(currentSection.extraConfigParsed)).map((cat, idx) => {
                const list = asList(currentSection.extraConfigParsed);
                const patch = (field, value) => {
                  const updated = [...list];
                  updated[idx] = { ...updated[idx], [field]: value };
                  handleUpdateExtra('high_silver_categories', updated);
                };
                const move = (dir) => {
                  const target = idx + dir;
                  if (target < 0 || target >= list.length) return;
                  const updated = [...list];
                  [updated[idx], updated[target]] = [updated[target], updated[idx]];
                  handleUpdateExtra('high_silver_categories', updated);
                };
                return (
                  <div key={cat.id ?? idx} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, background: '#f8fafc', display: 'grid', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>Category Card {idx + 1}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button type="button" onClick={() => move(-1)} disabled={idx === 0}
                          style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}>
                          <ArrowUp size={13} />
                        </button>
                        <button type="button" onClick={() => move(1)} disabled={idx === list.length - 1}
                          style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === list.length - 1 ? 'not-allowed' : 'pointer' }}>
                          <ArrowDown size={13} />
                        </button>
                        <button type="button" onClick={() => handleUpdateExtra('high_silver_categories', list.filter((_, i) => i !== idx))}
                          style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Category Name</label>
                        <input type="text" value={cat.label || cat.name || ''} onChange={(e) => patch('label', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Category Link</label>
                        <input type="text" value={cat.link || ''} onChange={(e) => patch('link', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Category Image</label>
                      <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                        <input type="text" value={cat.media || cat.mediaUrl || ''} onChange={(e) => patch('media', e.target.value)}
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }} />
                        <label style={{ background: '#2563eb', color: '#fff', padding: '8px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Upload size={12} /> Upload Photo
                          <input type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={(e) => e.target.files?.[0] && handleFileUploadForItem('high_silver_categories', idx, 'media', e.target.files[0])} />
                        </label>
                      </div>
                      {(cat.media || cat.mediaUrl) && (
                        <img src={resolveImgPreviewUrl(cat.media || cat.mediaUrl)} alt="Category" style={{ marginTop: 6, width: 48, height: 48, objectFit: 'cover', borderRadius: '50%', border: '1px solid #cbd5e1' }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'high_silver_collection' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  Custom Collection Items ({(asList(currentSection.extraConfigParsed)).length})
                </h3>
                <button
                  type="button"
                  onClick={() => handleUpdateExtra('high_silver_collection', [
                    ...(asList(currentSection.extraConfigParsed)),
                    {
                      id: Date.now(),
                      title: 'High Silver Item',
                      category: 'Earrings',
                      mediaUrl: '/images/editorial/story.jpg',
                      slug: '/jewellery'
                    }
                  ])}
                  style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Plus size={13} /> Add Custom Item
                </button>
              </div>

              {(asList(currentSection.extraConfigParsed)).map((item, idx) => {
                const list = asList(currentSection.extraConfigParsed);
                const patch = (field, value) => {
                  const updated = [...list];
                  updated[idx] = { ...updated[idx], [field]: value };
                  handleUpdateExtra('high_silver_collection', updated);
                };
                const move = (dir) => {
                  const target = idx + dir;
                  if (target < 0 || target >= list.length) return;
                  const updated = [...list];
                  [updated[idx], updated[target]] = [updated[target], updated[idx]];
                  handleUpdateExtra('high_silver_collection', updated);
                };
                return (
                  <div key={item.id ?? idx} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, background: '#f8fafc', display: 'grid', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>Item {idx + 1}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button type="button" onClick={() => move(-1)} disabled={idx === 0}
                          style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}>
                          <ArrowUp size={13} />
                        </button>
                        <button type="button" onClick={() => move(1)} disabled={idx === list.length - 1}
                          style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === list.length - 1 ? 'not-allowed' : 'pointer' }}>
                          <ArrowDown size={13} />
                        </button>
                        <button type="button" onClick={() => handleUpdateExtra('high_silver_collection', list.filter((_, i) => i !== idx))}
                          style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Item Title</label>
                        <input type="text" value={item.title || item.itemName || ''} onChange={(e) => patch('title', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Category Tag</label>
                        <input type="text" value={item.category || item.categoryName || ''} onChange={(e) => patch('category', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Product Link / Slug</label>
                      <input type="text" value={item.slug || item.link || ''} onChange={(e) => patch('slug', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                    </div>

                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Item Image</label>
                      <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                        <input type="text" value={item.mediaUrl || item.media || ''} onChange={(e) => patch('mediaUrl', e.target.value)}
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }} />
                        <label style={{ background: '#2563eb', color: '#fff', padding: '8px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Upload size={12} /> Upload Photo
                          <input type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={(e) => e.target.files?.[0] && handleFileUploadForItem('high_silver_collection', idx, 'mediaUrl', e.target.files[0])} />
                        </label>
                      </div>
                      {(item.mediaUrl || item.media) && (
                        <img src={resolveImgPreviewUrl(item.mediaUrl || item.media)} alt="Item" style={{ marginTop: 6, width: 60, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #cbd5e1' }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'blog_articles' && (() => {
            const storyList = Array.isArray(currentSection.extraConfigParsed) ? currentSection.extraConfigParsed : [];
            // Every other editor here edits the array in place; this keeps that habit
            const patchStory = (idx, field, value) => {
              const updated = [...storyList];
              updated[idx] = { ...updated[idx], [field]: value };
              handleUpdateExtra('blog_articles', updated);
            };
            return (
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#1e40af' }}>
                Every post on the Blog page. The photo alternates left and right by itself,
                so add as many as you like. Leave the link empty and the button is left off.
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  Blog Posts ({storyList.length})
                </h3>
                <button
                  type="button"
                  onClick={() => handleUpdateExtra('blog_articles', [
                    ...storyList,
                    {
                      category: 'Heritage & Craft',
                      title: 'New Blog Post',
                      excerpt: '',
                      date: '',
                      readTime: '',
                      href: '',
                      buttonText: 'Read Story',
                      mediaUrl: '',
                      quote: '',
                      quoteEyebrow: '',
                      isActive: true
                    }
                  ])}
                  style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Plus size={13} /> Add Post
                </button>
              </div>

              {storyList.map((story, idx) => (
                <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, background: '#f8fafc', display: 'grid', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>
                      Post {idx + 1}
                      <span style={{ fontWeight: 600, color: '#94a3b8', marginLeft: 6 }}>
                        {idx % 2 === 0 ? 'photo on the left' : 'photo on the right'}
                      </span>
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: story.isActive === false ? '#94a3b8' : '#16a34a', cursor: 'pointer' }}>
                        <input type="checkbox" checked={story.isActive !== false}
                          onChange={(e) => patchStory(idx, 'isActive', e.target.checked)} />
                        {story.isActive === false ? 'Hidden' : 'Live'}
                      </label>
                      <button type="button" onClick={() => handleMoveExtraItem('blog_articles', idx, -1)} disabled={idx === 0}
                        style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}>
                        <ArrowUp size={13} />
                      </button>
                      <button type="button" onClick={() => handleMoveExtraItem('blog_articles', idx, 1)}
                        style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}>
                        <ArrowDown size={13} />
                      </button>
                      <button type="button" onClick={() => handleUpdateExtra('blog_articles', storyList.filter((_, i) => i !== idx))}
                        style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Category</label>
                      <input type="text" value={story.category || ''} placeholder="Heritage & Craft"
                        onChange={(e) => patchStory(idx, 'category', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Date</label>
                      <input type="text" value={story.date || ''} placeholder="August 18, 2026"
                        onChange={(e) => patchStory(idx, 'date', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Post Title</label>
                    <input type="text" value={story.title || ''}
                      onChange={(e) => patchStory(idx, 'title', e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Excerpt</label>
                    <textarea rows={3} value={story.excerpt || ''}
                      onChange={(e) => patchStory(idx, 'excerpt', e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3, fontFamily: 'inherit' }} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Read Time</label>
                      <input type="text" value={story.readTime || ''} placeholder="5 min read"
                        onChange={(e) => patchStory(idx, 'readTime', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Button Text</label>
                      <input type="text" value={story.buttonText || ''} placeholder="Read Story"
                        onChange={(e) => patchStory(idx, 'buttonText', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Link (blank = no button)</label>
                      <input type="text" value={story.href || ''} placeholder="/blog/story-name"
                        onChange={(e) => patchStory(idx, 'href', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3 }} />
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Photo</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 3 }}>
                      <input type="text" value={story.mediaUrl || ''} placeholder="/images/editorial/..."
                        onChange={(e) => patchStory(idx, 'mediaUrl', e.target.value)}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5 }} />
                      <input
                        type="file"
                        accept="image/*"
                        id={'blogimg' + idx}
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUploadForItem('blog_articles', idx, 'mediaUrl', e.target.files?.[0])}
                      />
                      <label htmlFor={'blogimg' + idx}
                        style={{ background: '#0f172a', color: '#fff', borderRadius: 6, padding: '8px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Upload
                      </label>
                    </div>
                    {story.mediaUrl && (
                      <img src={resolveImgPreviewUrl(story.mediaUrl)} alt="" style={{ marginTop: 8, width: 150, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                    )}
                  </div>

                  <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: 10 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>
                      Black quote band AFTER this post
                    </label>
                    <textarea
                      rows={2}
                      value={story.quote || ''}
                      placeholder="Leave empty for no band after this post"
                      onChange={(e) => patchStory(idx, 'quote', e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, marginTop: 3, fontFamily: 'inherit' }}
                    />
                    <input
                      type="text"
                      value={story.quoteEyebrow || ''}
                      placeholder="Label above it (blank = Atelier Philosophy)"
                      onChange={(e) => patchStory(idx, 'quoteEyebrow', e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, marginTop: 6 }}
                    />
                  </div>
                </div>
              ))}
            </div>
            );
          })()}

          {activeTab === 'blog_quote' && (
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#1e40af' }}>
                Each post carries its own quote band - set it inside Blog Posts, under each post.
                What you set here is the label used when a post does not give one, the wording
                used after the first post, and the single switch that removes every band at once.
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Default Label</label>
                <input
                  type="text"
                  value={currentSection.badgeText || ''}
                  placeholder="Atelier Philosophy"
                  onChange={(e) => handleUpdateField('blog_quote', 'badgeText', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Quote after the first post</label>
                <textarea
                  rows={4}
                  value={currentSection.subtitle || ''}
                  onChange={(e) => handleUpdateField('blog_quote', 'subtitle', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4, fontFamily: 'inherit' }}
                />
                <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  Quotation marks are added by the page — do not type them.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'atelier_gallery' && (
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#1e40af' }}>
                Every tile in the Atelier visual archive. The filter tabs on the page are built from the
                categories used here - type a new category on any tile and its tab appears by itself.
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  Gallery Items ({(asList(currentSection.extraConfigParsed)).length})
                </h3>
                <button
                  type="button"
                  onClick={() => handleUpdateExtra('atelier_gallery', [
                    ...(asList(currentSection.extraConfigParsed)),
                    {
                      id: 'g' + Date.now(),
                      title: 'New Archive Photo',
                      category: 'Craft',
                      description: '',
                      mediaUrl: '',
                      location: '',
                      isActive: true
                    }
                  ])}
                  style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Plus size={13} /> Add Photo
                </button>
              </div>

              {(asList(currentSection.extraConfigParsed)).map((item, idx) => {
                const list = asList(currentSection.extraConfigParsed);
                const patch = (field, value) => {
                  const updated = [...list];
                  updated[idx] = { ...updated[idx], [field]: value };
                  handleUpdateExtra('atelier_gallery', updated);
                };
                const move = (dir) => {
                  const target = idx + dir;
                  if (target < 0 || target >= list.length) return;
                  const updated = [...list];
                  [updated[idx], updated[target]] = [updated[target], updated[idx]];
                  handleUpdateExtra('atelier_gallery', updated);
                };
                const preview = item.mediaUrl || item.image || '';
                return (
                  <div key={item.id ?? idx} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, background: '#f8fafc', display: 'grid', gridTemplateColumns: '96px 1fr', gap: 12, alignItems: 'start' }}>
                    <div>
                      <div style={{ width: 96, height: 72, borderRadius: 8, overflow: 'hidden', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {preview ? (
                          <img src={resolveImgPreviewUrl(preview)} alt={item.title || 'Archive'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <ImageIcon size={18} color="#475569" />
                        )}
                      </div>
                      <label style={{ marginTop: 6, background: '#2563eb', color: '#fff', padding: '6px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                        <Upload size={12} /> Upload
                        <input type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={(e) => e.target.files?.[0] && handleFileUploadForItem('atelier_gallery', idx, 'mediaUrl', e.target.files[0])} />
                      </label>
                    </div>

                    <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ fontSize: 11.5, fontWeight: 800, color: '#64748b' }}>Photo {idx + 1}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: item.isActive === false ? '#94a3b8' : '#16a34a', cursor: 'pointer' }}>
                            <input type="checkbox" checked={item.isActive !== false} onChange={(e) => patch('isActive', e.target.checked)} />
                            {item.isActive === false ? 'Hidden' : 'Live'}
                          </label>
                          <button type="button" onClick={() => move(-1)} disabled={idx === 0}
                            style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}>
                            <ArrowUp size={13} />
                          </button>
                          <button type="button" onClick={() => move(1)} disabled={idx === list.length - 1}
                            style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 7px', cursor: idx === list.length - 1 ? 'not-allowed' : 'pointer' }}>
                            <ArrowDown size={13} />
                          </button>
                          <button type="button" onClick={() => handleUpdateExtra('atelier_gallery', list.filter((_, i) => i !== idx))}
                            style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: 8 }}>
                        <input type="text" placeholder="Photo title" value={item.title || ''} onChange={(e) => patch('title', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5 }} />
                        <input type="text" list="atelier-cat-list" placeholder="Category" value={item.category || ''} onChange={(e) => patch('category', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5 }} />
                      </div>

                      <textarea rows={2} placeholder="Short description shown on hover" value={item.description || ''} onChange={(e) => patch('description', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5, fontFamily: 'inherit' }} />

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <input type="text" placeholder="Shoot location" value={item.location || ''} onChange={(e) => patch('location', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12.5 }} />
                        <input type="text" placeholder="/images/... or uploaded photo path" value={item.mediaUrl || item.image || ''} onChange={(e) => patch('mediaUrl', e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }} />
                      </div>
                    </div>
                  </div>
                );
              })}

              <datalist id="atelier-cat-list">
                {Array.from(new Set((asList(currentSection.extraConfigParsed)).map((x) => x.category).filter(Boolean))).map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>

              {(asList(currentSection.extraConfigParsed)).length === 0 && (
                <div style={{ textAlign: 'center', padding: 30, color: '#64748b', fontSize: 12.5, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10 }}>
                  No archive photos yet - the Atelier page will show only its masthead.
                </div>
              )}
            </div>
          )}

          {activeTab === 'atelier_marks' && (
            <SimpleListEditor
              items={currentSection.extraConfigParsed}
              onChange={(list) => handleUpdateExtra('atelier_marks', list)}
              itemLabel="Mark"
              addLabel="Add Mark"
              makeItem={() => ({ icon: 'check', title: 'New Mark', body: '', isActive: true })}
              fields={[
                { key: 'icon', label: 'Icon (check, scale, craft, renew, shield, crown, diamond, person, lock, truck)', width: 220 },
                { key: 'title', label: 'Mark Title' },
                { key: 'body', label: 'Short Line', full: true }
              ]}
            />
          )}

          {activeTab === 'atelier_process' && (
            <SimpleListEditor
              items={currentSection.extraConfigParsed}
              onChange={(list) => handleUpdateExtra('atelier_process', list)}
              itemLabel="Step"
              addLabel="Add Step"
              makeItem={() => ({ num: '', title: 'New Step', body: '', media: '', isActive: true })}
              fields={[
                { key: 'num', label: 'Number', width: 110 },
                { key: 'title', label: 'Step Name' },
                { key: 'body', label: 'Short Line' },
                { key: 'media', label: 'Step Image Path (e.g. /images/editorial/craft.jpg)', full: true }
              ]}
            />
          )}

          {activeTab === 'about_marks' && (
            <SimpleListEditor
              items={currentSection.extraConfigParsed}
              onChange={(list) => handleUpdateExtra('about_marks', list)}
              itemLabel="Mark"
              addLabel="Add Mark"
              makeItem={() => ({ icon: 'check', title: 'New Mark', body: '', isActive: true })}
              fields={[
                { key: 'icon', label: 'Icon (check, scale, craft, renew, shield, crown, diamond, person, lock, truck)', width: 220 },
                { key: 'title', label: 'Mark Title' },
                { key: 'body', label: 'Description', type: 'textarea', full: true }
              ]}
            />
          )}

          {activeTab === 'about_numbers' && (
            <SimpleListEditor
              items={currentSection.extraConfigParsed}
              onChange={(list) => handleUpdateExtra('about_numbers', list)}
              itemLabel="Number"
              addLabel="Add Number"
              makeItem={() => ({ value: '', label: 'New Number', isActive: true })}
              fields={[
                { key: 'value', label: 'Number (e.g. 925)', width: 150 },
                { key: 'label', label: 'What it means' }
              ]}
            />
          )}

          {activeTab === 'about_philosophy' && (
            <SimpleListEditor
              items={currentSection.extraConfigParsed}
              onChange={(list) => handleUpdateExtra('about_philosophy', list)}
              itemLabel="Pillar"
              addLabel="Add Pillar"
              makeItem={() => ({ num: '', title: 'New Pillar', body: '', isActive: true })}
              fields={[
                { key: 'num', label: 'Number', width: 110 },
                { key: 'title', label: 'Pillar Title' },
                { key: 'body', label: 'Description', type: 'textarea', full: true }
              ]}
            />
          )}

          {activeTab === 'about_timeline' && (
            <SimpleListEditor
              items={currentSection.extraConfigParsed}
              onChange={(list) => handleUpdateExtra('about_timeline', list)}
              itemLabel="Milestone"
              addLabel="Add Milestone"
              makeItem={() => ({ year: '', title: 'New Milestone', body: '', isActive: true })}
              fields={[
                { key: 'year', label: 'Year', width: 110 },
                { key: 'title', label: 'Milestone Title' },
                { key: 'body', label: 'Description', type: 'textarea', full: true }
              ]}
            />
          )}

          {activeTab === 'about_contact' && (
            <div style={{ display: 'grid', gap: 10, marginTop: 18, paddingTop: 18, borderTop: '1px dashed #cbd5e1' }}>
              <label style={{ fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase' }}>
                Second Button (concierge)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <input
                  type="text"
                  placeholder="Button text"
                  value={(!Array.isArray(currentSection.extraConfigParsed) && currentSection.extraConfigParsed?.secondaryText) || ''}
                  onChange={(e) => handleUpdateExtra('about_contact', {
                    ...(Array.isArray(currentSection.extraConfigParsed) ? {} : currentSection.extraConfigParsed || {}),
                    secondaryText: e.target.value
                  })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
                <input
                  type="text"
                  placeholder="WhatsApp or page link"
                  value={(!Array.isArray(currentSection.extraConfigParsed) && currentSection.extraConfigParsed?.secondaryLink) || ''}
                  onChange={(e) => handleUpdateExtra('about_contact', {
                    ...(Array.isArray(currentSection.extraConfigParsed) ? {} : currentSection.extraConfigParsed || {}),
                    secondaryLink: e.target.value
                  })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>
              <div style={{ fontSize: 11.5, color: '#64748b' }}>
                Leave the text empty to drop the second button. Band colours come from the Background / Text colour fields above.
              </div>
            </div>
          )}

          {activeTab === 'jewellery_seo_block' && (
            <div style={{ display: 'grid', gap: 10, marginTop: 18, paddingTop: 18, borderTop: '1px dashed #cbd5e1' }}>
              <label style={{ fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase' }}>
                SEO Keyword Chips
              </label>
              {(asList(currentSection.extraConfigParsed)).map((tag, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 36px', gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Label (e.g. Silver Earrings)"
                    value={tag.label || ''}
                    onChange={(e) => {
                      const updated = [...(asList(currentSection.extraConfigParsed))];
                      updated[idx] = { ...updated[idx], label: e.target.value };
                      handleUpdateExtra(activeTab, updated);
                    }}
                    style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12.5 }}
                  />
                  <select
                    value={tag.href || ''}
                    onChange={(e) => {
                      const updated = [...(asList(currentSection.extraConfigParsed))];
                      updated[idx] = { ...updated[idx], href: e.target.value };
                      handleUpdateExtra(activeTab, updated);
                    }}
                    style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12, background: '#fff' }}
                  >
                    {PAGE_LINK_OPTIONS.map((opt, oIdx) => (
                    <option key={oIdx} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => handleUpdateExtra(activeTab, (asList(currentSection.extraConfigParsed)).filter((_, i) => i !== idx))}
                    style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, cursor: 'pointer' }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => handleUpdateExtra(activeTab, [...(asList(currentSection.extraConfigParsed)), { label: 'New Keyword', href: '/jewellery' }])}
                style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, justifySelf: 'start' }}
              >
                <Plus size={13} /> Add Keyword Chip
              </button>
            </div>
          )}

        </div>

        {showPreview && (
          <div style={{ height: 'calc(100vh - 150px)', minHeight: 560, position: 'sticky', top: 16, minWidth: 0 }}>
            <CmsPreviewPane
              pagePath={previewPagePath}
              sections={sections}
              activeSectionKey={activeTab}
              reloadToken={previewReloadToken}
              productOrder={previewProductOrder}
              forcePanel={previewPanel}
            />
          </div>
        )}
      </div>

      {cropperModalData && (
        <ImageCropperModal
          imageSrc={cropperModalData.imageSrc}
          title={cropperModalData.title || 'Crop & Resize Image'}
          initialAspect={cropperModalData.aspect || 1}
          onClose={() => setCropperModalData(null)}
          onCropComplete={(croppedData) => {
            if (cropperModalData.onCrop && croppedData?.file) {
              cropperModalData.onCrop(croppedData.file);
            }
            setCropperModalData(null);
          }}
        />
      )}
    </div>
  );
}
